/* Cart drawer — Cannumo 3.0.
 *
 * Never runs on /cart: that page serves abandoned-checkout links and checkout
 * returns, where hijacking the view would break the journey we sent people on.
 *
 * Entitlement rules are copied from the live drawer and are CART-BASED, never
 * page-based — a customer who earned a gift on a BOGO page keeps it while
 * browsing elsewhere. Getting this wrong silently removes gifts people earned,
 * so the checks read the cart lines and nothing else.
 */
(function () {
  'use strict';

  var cfg = window.CD_CONFIG;
  var root = document.querySelector('[data-cd-root]');
  if (!cfg || !root) return;                       // not rendered
  // Page mode: the identical component rendered inline on /cart (see
  // sections/main-cart.liquid). It skips the overlay behaviours — open/close,
  // scroll lock, focus trap — and renders on load instead of on open.
  var pageMode = root.hasAttribute('data-cd-page');
  if (window.CD_PAGE_TEMPLATE === 'cart' && !pageMode) return;  // /cart, no page host

  var panel   = root.querySelector('.cd__panel');
  var bodyEl  = root.querySelector('[data-cd-body]');
  var footEl  = root.querySelector('[data-cd-foot]');
  var progEl  = root.querySelector('[data-cd-progress]');

  var state = { cart: null, busy: false, lastFocus: null, email: null, discountCode: null };

  var GIFT_IDS = new Set(
    [].concat(cfg.freeGiftVariantIds || [], cfg.bogoGiftVariantIds || [], cfg.retiredGiftVariantIds || [], [cfg.v3PinVariantId])
      .filter(Boolean).map(Number)
  );

  /* ── helpers ─────────────────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents, currency) {
    var value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency || 'GBP',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2
      }).format(value);
    } catch (e) {
      return (currency === 'USD' ? '$' : '£') + value.toFixed(2);
    }
  }

  function thresholds(currency) {
    return (cfg.thresholds && cfg.thresholds[currency]) || cfg.thresholds.GBP;
  }

  function isGift(line) {
    return GIFT_IDS.has(Number(line.variant_id)) || !!(line.properties && line.properties._gift);
  }

  function lineOffer(line) {
    return String((line.properties && line.properties._offer) || '');
  }

  function linePlanId(line) {
    var a = line.selling_plan_allocation;
    return String((a && a.selling_plan && a.selling_plan.id) || '');
  }

  /* Which offer "owns" the cart — expressed as small predicates the gift syncs
     share, so BOGO and the subscription-freebie sync never disagree about the
     cart's state. All CART-based, never page-based. */
  function cartHasNewPlan(cart) {
    var items = (cart && cart.items) || [];
    var newPlans = new Set((cfg.newPlanIds || []).map(String));
    return items.some(function (l) { return newPlans.has(linePlanId(l)); });
  }
  function cartHasV3Offer(cart) {
    var items = (cart && cart.items) || [];
    return items.some(function (l) { return lineOffer(l) === cfg.v3OfferTag; });
  }
  /* The plan-gift offer (goodie-e style: subscribe + welcome gifts, no free
     bottle). Tagged on paid lines via properties._offer at ATC. It still WANTS
     the subscription welcome freebies (syncFreeGifts grants them), but must
     suppress the BOGO free bottle. */
  function cartHasPlanGift(cart) {
    var items = (cart && cart.items) || [];
    var tag = cfg.planGiftTag || 'plan-gift';
    return items.some(function (l) { return lineOffer(l) === tag; });
  }
  /* The offer this cart was built under, as defined in cfg.offers. First tag
     that names a known offer wins; an untagged cart has none and keeps every
     default. */
  function cartOffer(cart) {
    var defs = cfg.offers || {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var tag = lineOffer(items[i]);
      if (tag && defs[tag]) return defs[tag];
    }
    return null;
  }

  function hasSubscription(cart) { return cadenceOf(cart) > 0; }

  /* "Simple" offers (goodiev42a, goodiev42b): the offer's own -offer
     products, subscribe on one plan (a first-order discount, free extras on
     42b) or buy once. Shipping on those products is never free by cart value:
     only the offer's delivery code (offers.X.shipCode) frees it, and only with
     a subscription. Everything that reads offer.simple is theirs alone; every
     other offer keeps its own path. */
  function simpleOffer(cart) {
    var o = cartOffer(cart);
    return o && o.simple ? o : null;
  }
  function simpleSubscribed(cart, offer) {
    return !!offer && offerLines(cart, offer, 'sub').length > 0;
  }
  /* Codes the drawer owns on simple offers: the delivery code and any coded
     gift's code (offers.X.codedGifts: a paid product made free by a promo
     code, e.g. 42b's ceramic shot + "Bonus"). */
  function offerCodeList() {
    var out = [];
    var defs = cfg.offers || {};
    Object.keys(defs).forEach(function (k) {
      if (defs[k].shipCode) out.push(String(defs[k].shipCode).toLowerCase());
      (defs[k].codedGifts || []).forEach(function (g) { if (g.code) out.push(String(g.code).toLowerCase()); });
    });
    return out;
  }
  /* The codes a simple offer wants on the cart: all of them while subscribed
     (its gifts are subscription-only), none otherwise. */
  function offerCodesWanted(cart, offer) {
    if (!offer || !simpleSubscribed(cart, offer)) return [];
    var out = offer.shipCode ? [String(offer.shipCode)] : [];
    (offer.codedGifts || []).forEach(function (g) { if (g.code) out.push(String(g.code)); });
    return out;
  }
  function isOfferCode(code) { return !!code && offerCodeList().indexOf(String(code).toLowerCase()) > -1; }
  /* Delivery codes on the cart now, to carry along whenever the drawer writes
     the cart's codes (a customer's own code must not knock them off). Empty
     for every cart that is not on a simple offer. */
  function offerCodesOn(cart) {
    return ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code); }).filter(isOfferCode);
  }
  /* The offer whose codes the drawer keeps on the cart: a simple offer, or any
     other offer with a shipCode (benchmark: Buy 1 Get 1 on the normal products,
     free shipping with the subscription through the same delivery code). */
  function codeOffer(cart) {
    var o = cartOffer(cart);
    return o && (o.simple || o.shipCode) ? o : null;
  }
  /* A non-simple offer with a shipCode ships free while it is subscribed, with
     no £50 tier needed; its one-time purchase keeps the usual £50 rule. */
  function offerShipsFree(cart) {
    var o = cartOffer(cart);
    return !!(o && !o.simple && o.shipCode && simpleSubscribed(cart, o));
  }

  /* A cart opts out of BOGO when it carries another offer that ships its own
     gifts — an A/B plan line, a v3-3pack line, or a plan-gift line. */
  function cartOptsOutOfBogo(cart) {
    return cartHasNewPlan(cart) || cartHasV3Offer(cart) || cartHasPlanGift(cart);
  }

  /* Months between deliveries of the cart's longest plan, from the shared
     table or any offer's own plans (keys are months in both). 0 = no
     subscription. Plans the drawer does not know (the A/B newPlanIds) still
     read 0, as before. */
  function cadenceOf(cart) {
    var found = 0;
    ((cart && cart.items) || []).forEach(function (l) {
      var p = planEntry(linePlanId(l));
      if (p) found = Math.max(found, Number(p.key));
    });
    return found;
  }

  /* ── offer plan tables ─────────────────────────────────────────
     An offer can sell its own subscription plans (cfg.offers.X.plans), same
     shape as cfg.cadences: keyed by months between deliveries, each with the
     bottles per delivery. Plan ids are unique store-wide, so a line's plan is
     resolved by searching the shared table first and then every offer's:
     labels, "was" prices and "is this a subscription" work for any plan,
     whatever page the cart came from. Shared-table carts resolve exactly as
     they always did. */
  function planEntry(planId) {
    planId = String(planId || '');
    if (!planId) return null;
    var tables = [{ offer: '', table: cfg.cadences }];
    Object.keys(cfg.offers || {}).forEach(function (k) {
      if (cfg.offers[k].plans) tables.push({ offer: k, table: cfg.offers[k].plans });
    });
    for (var i = 0; i < tables.length; i++) {
      var t = tables[i].table;
      var key = Object.keys(t).find(function (kk) { return String(t[kk].plan) === planId; });
      if (key) return { key: key, entry: t[key], table: t, offer: tables[i].offer };
    }
    return null;
  }
  function offerKey(offer) {
    var defs = cfg.offers || {};
    return Object.keys(defs).find(function (k) { return defs[k] === offer; }) || '';
  }
  /* Per-bottle price of a plan, straight from the store's selling plan
     allocations (rendered into cfg.planPrices), so it can never drift from
     what Appstle charges. */
  function planPerBottle(planId) {
    var p = (cfg.planPrices || {})[String(planId)];
    return p != null ? Number(p) : 0;
  }
  /* Paid, non-gift lines carrying this offer's tag, split by purchase type. */
  function offerLines(cart, offer, kind) {
    var tag = offerKey(offer);
    return ((cart && cart.items) || []).filter(function (l) {
      if (isGift(l) || !tag || lineOffer(l) !== tag) return false;
      if (kind === 'once') return !linePlanId(l);
      if (kind === 'sub') return !!linePlanId(l);
      return true;
    });
  }
  function qtyOf(lines) { return lines.reduce(function (a, l) { return a + Number(l.quantity || 0); }, 0); }
  /* The flavours an offer pre-selects, in order (cfg.flavourCycle: raspberry,
     blackcurrant, sea buckthorn), the same order the offer pages use: 2 bottles
     = the first two, 3 = one of each, 6 = two of each. New slots cycle it. */
  function flavourCycle() {
    return (cfg.flavourCycle || []).map(Number).filter(function (v) { return v > 0; });
  }

  /* One-time pack price (offers.X.packs: [{ min, pct }]). The discount itself
     is applied by the store's Functions app, whose rules mirror this table; the
     drawer works out the same number so the price shown never differs from
     checkout. If Shopify has already applied it in the cart (the lines carry
     discount allocations), only the part still missing is added on top, so it
     can never be counted twice. */
  function packDiscount(cart) {
    var none = { qty: 0, base: 0, tier: null, next: null, expected: 0, pending: 0 };
    var offer = cartOffer(cart);
    if (!offer || !offer.packs || !offer.packs.length) return none;
    var lines = offerLines(cart, offer, 'once');
    if (!lines.length) return none;
    var qty = qtyOf(lines);
    var base = lines.reduce(function (a, l) { return a + Number(l.original_line_price || l.line_price || 0); }, 0);
    // What Shopify has already taken off these lines for the pack: its pack
    // code (or an automatic rule), never a customer's own code.
    var applied = lines.reduce(function (a, l) {
      return a + (l.line_level_discount_allocations || []).reduce(function (b, d) {
        var app = d.discount_application || {};
        // cart.js gives the code in title; key is an internal id.
        var isPack = app.type === 'automatic' || (app.type === 'discount_code' && (isPackCode(app.title) || isPackCode(app.key)));
        return isPack ? b + Number(d.amount || 0) : b;
      }, 0);
    }, 0);
    var tiers = offer.packs.slice().sort(function (a, b) { return a.min - b.min; });
    var tier = null, next = null;
    tiers.forEach(function (t) { if (qty >= t.min) tier = t; else if (!next) next = t; });
    // A tier with a fixed amount (its code's value, once per order) uses it as
    // is; otherwise the percentage of the pack lines.
    var expected = !tier ? 0 : (tier.off != null ? Math.round(Number(tier.off) * 100) : Math.round(base * Number(tier.pct) / 100));
    /* Once Shopify applies the discount (the app's rule is live), its amount is
       the truth, to the penny: shown = applied, nothing pending. Before that,
       the drawer's own figure is shown and taken off the total. */
    if (tier && applied > 0) return { qty: qty, base: base, tier: tier, next: next, expected: applied, applied: applied, pending: 0 };
    return { qty: qty, base: base, tier: tier, next: next, expected: expected, applied: applied, pending: expected };
  }

  /* Pack discount codes (offers.X.packs[].code): static Shopify codes, one per
     tier, set on the cart so Shopify itself prices the pack (cart, drawer and
     checkout alike). Never shown or treated as the customer's own code. */
  function packCodeList() {
    var out = [];
    var defs = cfg.offers || {};
    Object.keys(defs).forEach(function (k) {
      (defs[k].packs || []).forEach(function (t) { if (t.code) out.push(String(t.code).toLowerCase()); });
    });
    return out;
  }
  function isPackCode(code) { return !!code && packCodeList().indexOf(String(code).toLowerCase()) > -1; }

  /* Keeps exactly the right pack code on the cart: the tier the one-time pack
     lines reach, none otherwise (subscribed, emptied, another offer). Other
     codes on the cart are kept. Returns true when it changed the cart. */
  async function syncPackCode(cart) {
    if (!packCodeList().length) return false;
    var pk = packDiscount(cart);
    var want = pk.tier && pk.tier.code ? String(pk.tier.code) : '';
    var have = ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code); });
    var havePack = have.filter(isPackCode);
    var others = have.filter(function (c) { return !isPackCode(c); });
    if (want && havePack.length === 1 && havePack[0].toLowerCase() === want.toLowerCase()) return false;
    if (!want && !havePack.length) return false;
    try {
      await postCart('/cart/update.js', { discount: others.concat(want ? [want] : []).join(',') });
      return true;
    } catch (e) { return false; }
  }

  /* Simple offers' delivery code (SUBSCRIPTION DELIVERY): on the cart while
     it holds that offer's subscription, off it otherwise (switched to
     one-time, removed, emptied). Silent: never shown as the customer's code,
     and every other code on the cart is kept. The page adds it at ATC too, so
     it holds when the drawer is skipped. Returns true when it changed the cart. */
  async function syncOfferCodes(cart) {
    if (!offerCodeList().length) return false;
    var want = offerCodesWanted(cart, codeOffer(cart));
    var have = ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code); });
    var haveOffer = have.filter(isOfferCode);
    var others = have.filter(function (c) { return !isOfferCode(c); });
    var norm = function (list) { return list.map(function (c) { return c.toLowerCase(); }).sort().join(','); };
    if (norm(haveOffer) === norm(want)) return false;
    try {
      await postCart('/cart/update.js', { discount: others.concat(want).join(',') });
      return true;
    } catch (e) { return false; }
  }

  /* Hidden gift: exactly one while the cart holds a paid subscription line,
     none otherwise. Out of stock = never added: the page renders it unavailable,
     or Shopify refuses the add and the session stops asking. Returns true when
     it changed the cart. */
  var hiddenSkip = false;
  try { hiddenSkip = sessionStorage.getItem('cd-hidden-gift-skip') === '1'; } catch (e) {}
  async function syncHiddenGift(cart) {
    var hg = cfg.hiddenGift;
    if (!hg || !hg.variantId || !cart) return false;
    var have = cart._hidden || [];
    var subscribed = (cart.items || []).some(function (l) { return !isGift(l) && !!linePlanId(l); });
    var want = subscribed && hg.available !== false && !hiddenSkip;
    try {
      if (!want) {
        if (!have.length) return false;
        await removeLines(have);
        return true;
      }
      if (have.length === 1 && Number(have[0].quantity) === 1) return false;
      if (have.length) {
        var upd = {};
        have.forEach(function (l, i) { upd[l.key] = i === 0 ? 1 : 0; });
        await postCart('/cart/update.js', { updates: upd });
        return true;
      }
      await postCart('/cart/add.js', { items: [{ id: Number(hg.variantId), quantity: 1 }] });
      return true;
    } catch (e) {
      // Out of stock (Shopify answers 422): stop asking this session. Other errors retry next time.
      if (!have.length && /_422$/.test(String(e && e.message))) { hiddenSkip = true; try { sessionStorage.setItem('cd-hidden-gift-skip', '1'); } catch (er) {} }
      return false;
    }
  }

  /* Coded gifts (simple offers): the product line, marked _gift = the offer,
     one of each while the offer is subscribed, none otherwise. Its code is
     kept by syncOfferCodes, which runs after this. Returns true on change. */
  async function syncCodedGifts(cart) {
    var items = (cart && cart.items) || [];
    var so = codeOffer(cart);   // simple offers and shipCode offers (benchmark-lockin's cup)
    var tag = so ? offerKey(so) : '';
    var want = so && simpleSubscribed(cart, so) ? (so.codedGifts || []) : [];
    var lines = items.filter(function (l) { return l.properties && l.properties._gift; });
    var updates = {}, changed = false;
    lines.forEach(function (l) {
      var ok = String(l.properties._gift) === tag && want.some(function (g) { return Number(g.variantId) === Number(l.variant_id); });
      if (!ok) { updates[l.key] = 0; changed = true; }
      else if (Number(l.quantity) !== 1) { updates[l.key] = 1; changed = true; }
    });
    var add = want.filter(function (g) {
      return !lines.some(function (l) { return Number(l.variant_id) === Number(g.variantId) && String(l.properties._gift) === tag; });
    }).map(function (g) { return { id: Number(g.variantId), quantity: 1, properties: { _gift: tag } }; });
    try {
      if (changed) await postCart('/cart/update.js', { updates: updates });
      if (add.length) await postCart('/cart/add.js', { items: add });
    } catch (e) { return false; }
    return changed || add.length > 0;
  }

  /* ── open / close ────────────────────────────────────────────── */

  function open() {
    // Inline on the cart page: always visible, nothing to slide in — just render.
    if (pageMode) { root.hidden = false; refresh(); return; }
    if (!root.hidden && root.classList.contains('is-open')) return;
    state.lastFocus = document.activeElement;
    root.hidden = false;
    // next frame so the transition runs from the off-screen position
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    // Lock BOTH: body alone leaves the page scrollable when html is the
    // scroll container, which shows as a second scrollbar beside the drawer.
    document.documentElement.classList.add('cd-locked');
    var focusable = panel.querySelector('button, [href], input, select, textarea');
    if (focusable) focusable.focus({ preventScroll: true });
    refresh();
  }

  function close() {
    if (pageMode) return;   // the page panel is never dismissed
    root.classList.remove('is-open');
    document.documentElement.classList.remove('cd-locked');
    var done = function () { root.hidden = true; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 260);
    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
  }

  function trapFocus(e) {
    if (pageMode) return;   // never trap keyboard focus inside an inline page panel
    if (e.key !== 'Tab' || root.hidden) return;
    var nodes = panel.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ── data ────────────────────────────────────────────────────── */

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  /* Shopify rate-limits /cart writes (429). A switch is a remove+re-add burst,
     so a bare failure could leave the cart half-switched — retry with backoff.
     Ported from the live drawer (postJson/fetchCart backoff). */
  async function getCart() {
    var delays = [500, 1000, 2000];
    for (var attempt = 0; ; attempt++) {
      var res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (res.status === 429 && attempt < delays.length) { await sleep(delays[attempt]); continue; }
      if (!res.ok) throw new Error('cart_fetch_failed');
      return stripHidden(await res.json());
    }
  }

  /* The hidden gift (cfg.hiddenGift) is taken out of every cart object the
     drawer works with - lines, item_count - and kept aside on cart._hidden for
     syncHiddenGift. It is £0, so the totals are unchanged. */
  function stripHidden(c) {
    var hg = cfg.hiddenGift;
    if (!c || !c.items || !hg || !hg.variantId) return c;
    var id = Number(hg.variantId);
    var hidden = c.items.filter(function (l) { return Number(l.variant_id) === id; });
    c._hidden = hidden;
    if (!hidden.length) return c;
    c.items = c.items.filter(function (l) { return Number(l.variant_id) !== id; });
    c.item_count = Math.max(0, Number(c.item_count || 0) - hidden.reduce(function (a, l) { return a + Number(l.quantity || 0); }, 0));
    return c;
  }

  /* Removes several lines in ONE request. Sequential change.js calls go stale:
     once a discount code is on the cart, Shopify can re-key the remaining lines
     after each write, and the later removals miss. */
  async function removeLines(lines) {
    if (!lines || !lines.length) return;
    var updates = {};
    lines.forEach(function (l) { updates[l.key] = 0; });
    await postCart('/cart/update.js', { updates: updates });
  }

  async function postCart(url, payload) {
    var delays = [300, 600];
    for (var attempt = 0; ; attempt++) {
      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      if (res.status === 429 && attempt < delays.length) { await sleep(delays[attempt]); continue; }
      if (!res.ok) throw new Error('cart_write_failed_' + res.status);
      var out = await res.json();
      return out && out.token && out.items ? stripHidden(out) : out;
    }
  }

  async function refresh() {
    try {
      state.cart = await getCart();
      // Reconcile gift lines before rendering so the customer never sees a
      // gift flash in or out after the drawer has already painted.
      // Orphan guard first: once the last paid item is gone, no free gift may
      // linger (BOGO bottle, subscription freebies, v3 pin) — otherwise the
      // cart could check out as gifts-only.
      if (await stripOrphanGifts(state.cart)) state.cart = await getCart();
      if (await syncFreeGifts(state.cart)) state.cart = await getCart();
      if (await syncBogo(state.cart)) state.cart = await getCart();
      if (await syncPackCode(state.cart)) state.cart = await getCart();
      if (await syncCodedGifts(state.cart)) state.cart = await getCart();
      if (await syncOfferCodes(state.cart)) state.cart = await getCart();
      if (await syncHiddenGift(state.cart)) state.cart = await getCart();
      await syncOfferAttr(state.cart);
      var attrs = state.cart.attributes || {};
      var justLoadedCode = false;
      var linked = cartCode(state.cart);
      if (state.discountCode === null && linked) {
        // Arrived through a /discount/CODE link: Shopify already holds the code
        // and prices the cart with it, so it is shown as-is and never cleared
        // on a failed proxy check (an empty cart or an unmet minimum reads as
        // not applicable until it is met).
        state.discountCode = linked;
      } else if (state.discountCode === null && attrs.discount_code) {
        state.discountCode = String(attrs.discount_code);
        justLoadedCode = true;
      }
      render(state.cart);
      // Re-validate a freshly-loaded saved code against the proxy and drop it if
      // it no longer applies (expired, cart no longer qualifies, code disabled).
      // Runs after the first paint so it never delays showing the cart. A null
      // result (proxy unreachable) leaves the code untouched — only an explicit
      // discountApplicable:false clears it.
      if (justLoadedCode) {
        fetchProxyCart(state.cart, true).then(function (check) {
          if (check && check.discountApplicable === false) {
            state.discountCode = null;
            invalidateProxy();
            persistAttrs({ discount_code: '' });
            render(state.cart);
          }
        });
      }
    } catch (e) {
      bodyEl.innerHTML = '<p class="cd-empty__body" style="padding:2rem 0;text-align:center">'
        + 'We could not load your cart. Please refresh the page.</p>';
      footEl.hidden = true;
      progEl.hidden = true;
    }
  }

  async function act(fn) {
    if (state.busy) return;
    state.busy = true;
    root.classList.add('is-busy');
    try { await fn(); await refresh(); }
    catch (e) { await refresh(); }
    finally { state.busy = false; root.classList.remove('is-busy'); }
  }

  /* ── progress ────────────────────────────────────────────────── */
  /* Ported from the prototype's model, which is stronger than the flat bar I
     first built. Fixed milestones (the 2-month / 30%-off tier is discontinued):
       1  Free shipping  (cart value ≥ £50)
       2  20% off        (any subscription   — cadence ≥ 1)
       3  40% off        (3-month plan        — cadence ≥ 3)
     The 20/30/40 come from the PLAN, not from spending more — so this is still
     not the Figma spend-ladder. Milestones gate sequentially: free shipping
     must be reached before a sub tier lights, which keeps the fill honest (a
     £39 monthly cart reads "20% off ready", not a bar jumping past an unlit
     free-ship dot). The message always states the next action. */
  /* Offers with their own ladder (fermented: free bottles on one-time, its own
     plans on subscription; fermented2: pack prices on one-time, its own plans)
     get their own steps. Free shipping stays the first step everywhere. Every
     other cart takes the shared model below, unchanged. */
  /* Does this offer give free bottles to this kind of line ('once' | 'sub')?
     Mirrors offerFreeBottles: subscription by default, one-time with oneTime,
     one-time only with freeOn: 'once'. */
  function freeAppliesTo(offer, kind) {
    var t = offer && offer.freeBottles;
    if (!t || !Object.keys(t).some(function (k) { return Number(t[k]) > 0; })) return false;
    if (offer.freeOn === 'once') return kind === 'once';
    return kind === 'sub' || !!offer.oneTime;
  }
  /* Free bottles earned at the plan step with this many paid bottles. */
  function freeAt(offer, qty) {
    var t = offer.freeBottles || {};
    var keys = Object.keys(t).map(Number).sort(function (a, b) { return a - b; });
    var hit = 0;
    keys.forEach(function (k) { if (qty >= k) hit = Number(t[k] || 0); });
    return hit;
  }

  /* "monthly plan" / "3-month plan", from a plan table key (months). */
  function planName(k, e) {
    if (e && e.planName) return e.planName;
    return Number(k) === 1 ? 'monthly plan' : Number(k) + '-month plan';
  }
  function moreBottles(n) { return n + ' more bottle' + (n === 1 ? '' : 's'); }

  /* Steps carry two hints, both one line in the drawer: hint on its own once
     free shipping is reached (the bar already shows that), short after the
     free-shipping gap while it is not. */
  function offerLadder(cart, offer) {
    var subLines = offerLines(cart, offer, 'sub');
    var steps = [];
    if (subLines.length && offer.plans && freeAppliesTo(offer, 'sub')) {
      // Subscription with free bottles (fermented): the steps are the plans
      // that earn more free bottles on the first order.
      var paidS = qtyOf(subLines);
      Object.keys(offer.plans).map(Number).sort(function (a, b) { return a - b; }).forEach(function (k) {
        var e = offer.plans[k], free = freeAt(offer, Number(e.qty));
        if (!free) return;
        steps.push({ label: free + ' free', on: paidS >= Number(e.qty),
                     hint: 'Upgrade to <b>' + planName(k, e) + '</b> and get ' + free + ' free',
                     short: 'upgrade, get ' + free + ' free' });
      });
      return { steps: steps, done: 'All offers unlocked' };
    }
    if (subLines.length && offer.plans) {
      var cur = planEntry(linePlanId(subLines[0]));
      var curKey = cur ? Number(cur.key) : 0;
      Object.keys(offer.plans).map(Number).sort(function (a, b) { return a - b; }).slice(1).forEach(function (k) {
        var e = offer.plans[k];
        steps.push({ label: '−' + Number(e.pct || 0) + '%', on: curKey >= k,
                     hint: 'Upgrade to <b>' + planName(k, e) + '</b> to save ' + Number(e.pct || 0) + '% per bottle',
                     short: 'upgrade to save ' + Number(e.pct || 0) + '%' });
      });
      return { steps: steps, done: 'All savings unlocked' };
    }
    var paid = qtyOf(offerLines(cart, offer, 'once'));
    if (offer.packs) {
      offer.packs.slice().sort(function (a, b) { return a.min - b.min; }).forEach(function (t) {
        var need = t.min - paid;
        steps.push({ label: '−' + Math.round(Number(t.pct)) + '%', on: paid >= t.min,
                     hint: 'Add <b>' + moreBottles(need) + '</b> to save ' + Math.round(Number(t.pct)) + '%',
                     short: 'add ' + need + ' more to save ' + Math.round(Number(t.pct)) + '%' });
      });
    } else if (offer.freeBottles && freeAppliesTo(offer, 'once')) {
      Object.keys(offer.freeBottles).map(Number).sort(function (a, b) { return a - b; }).forEach(function (k) {
        var free = Number(offer.freeBottles[k] || 0);
        if (!free) return;
        var need = k - paid;
        steps.push({ label: free + ' free', on: paid >= k,
                     hint: 'Add <b>' + moreBottles(need) + '</b> and get ' + free + ' free',
                     short: 'add ' + need + ', get ' + free + ' free' });
      });
    }
    /* Last step on a one-time basket: move to the matching subscription, at
       its real saving per bottle (free bottles included). */
    var tgtL = offerSubTarget(cart, offer);
    if (tgtL) {
      var effL = subEffective(offer, tgtL.entry);
      steps.push({ label: 'Subscribe', on: false,
                   hint: '<b>Subscribe</b> to save ' + effL.pct + '% per bottle',
                   short: 'subscribe to save ' + effL.pct + '%' });
    }
    return { steps: steps, done: 'All offers unlocked' };
  }

  function buildMilestones(cart) {
    var offer = cartOffer(cart);
    if (offer && offer.simple) return buildSimpleMilestones(cart, offer);
    if (offer && (offer.plans || offer.packs)) return buildOfferMilestones(cart, offer);
    return buildSharedMilestones(cart);
  }

  /* Simple offers: everything comes with the subscription (the first-order
     discount, free shipping, 42b's free extras), and nothing with spend, so
     the steps light together and the message is the one action that counts. */
  function buildSimpleMilestones(cart, offer) {
    var on = simpleSubscribed(cart, offer);
    var defs = [{ label: offer.dealMark || 'Subscribe', on: on }, { label: 'Free shipping', on: on }];
    if (offer.extrasMark) defs.push({ label: offer.extrasMark, on: on });
    var marks = defs.map(function (d, i, arr) {
      return { pos: Math.round(((i + 1) / arr.length) * 100), label: d.label, on: d.on };
    });
    var msg = on
      ? '<b>All offers unlocked</b> &middot; ' + (offer.doneText || 'free shipping included')
      : '<b>Subscribe</b> for ' + (offer.pitch || 'free shipping');
    return { marks: marks, pct: on ? 100 : 0, msg: msg, freeHit: on,
             reachedCount: on ? marks.length : 0 };
  }

  function buildOfferMilestones(cart, offer) {
    var cur = cart.currency || 'GBP';
    var freeAt = thresholds(cur).freeShipping * 100;
    var goods = Number(cart.total_price || 0) - packDiscount(cart).pending;
    var freeHit = goods >= freeAt || codeIsFreeShipping(cart) || offerShipsFree(cart);
    var ladder = offerLadder(cart, offer);
    var defs = [{ label: 'Free shipping', on: freeHit }].concat(ladder.steps);
    var marks = defs.map(function (d, i, arr) {
      return { pos: Math.round(((i + 1) / arr.length) * 100), label: d.label, on: d.on };
    });
    var pct = 0;
    for (var i = 0; i < marks.length; i++) { if (marks[i].on) pct = marks[i].pos; else break; }
    if (pct === 0 && goods > 0) pct = Math.min(marks[0].pos, (goods / freeAt) * marks[0].pos);
    var next = ladder.steps.find(function (s) { return !s.on; });
    var msg;
    if (!freeHit) {
      msg = '<b>' + esc(money(Math.max(0, freeAt - goods), cur)) + '</b> to free shipping'
          + (next ? ' &middot; ' + next.short : '');
    } else if (next) {
      msg = next.hint;
    } else {
      msg = '<b>' + ladder.done + '</b> &middot; free shipping included';
    }
    return { marks: marks, pct: pct, msg: msg, freeHit: freeHit,
             reachedCount: marks.filter(function (m) { return m.on; }).length };
  }

  function buildSharedMilestones(cart) {
    var cur = cart.currency || 'GBP';
    var t = thresholds(cur);
    var sub = Number(cart.total_price || 0);
    var freeAt = t.freeShipping * 100;
    // A free-shipping code unlocks it too, and so does a subscription on an offer
    // that ships free with it (offerShipsFree).
    var freeHit = sub >= freeAt || codeIsFreeShipping(cart) || offerShipsFree(cart);
    var cad = cadenceOf(cart);

    // Once the customer is subscribed, 20% is already theirs, so that step is
    // dropped and the bar shows the targets still ahead. Positions redistribute
    // evenly across whatever milestones remain.
    var defs = [
      { key: 'ship', label: 'Free shipping', on: freeHit },
      { key: 't20',  label: '20% off',   on: freeHit && cad >= 1, tier: 1 },
      { key: 't40',  label: '40% off',   on: freeHit && cad >= 3, tier: 3 }
    ];
    if (cad >= 1) defs = defs.filter(function (d) { return d.key !== 't20'; });
    // noUpgrade offers sell the monthly plan only: no 40% / 3-month step.
    var noUp = !!(cartOffer(cart) || {}).noUpgrade;
    if (noUp) defs = defs.filter(function (d) { return d.key !== 't40'; });

    var marks = defs.map(function (d, i, arr) {
      return { pos: Math.round(((i + 1) / arr.length) * 100), label: d.label, on: d.on };
    });

    var pct = 0;
    for (var i = 0; i < marks.length; i++) { if (marks[i].on) pct = marks[i].pos; else break; }
    if (pct === 0 && sub > 0) pct = Math.min(marks[0].pos, (sub / freeAt) * marks[0].pos);

    var gap = esc(money(Math.max(0, freeAt - sub), cur));
    var msg;
    if (!freeHit) {
      if (sub === 0) msg = 'Add <b>' + esc(money(freeAt, cur)) + '</b> for free UK shipping';
      else if (cad >= 3) msg = '<b>' + gap + '</b> away from free shipping &middot; 40% off ready';
      else if (cad >= 1) msg = '<b>' + gap + '</b> away from free shipping &middot; 20% off ready';
      else msg = '<b>' + gap + '</b> away from free UK shipping';
    } else if (cad < 1) {
      msg = 'Free shipping unlocked &middot; <b>Subscribe</b> to save 20%';
    } else if (cad < 3 && noUp) {
      msg = '<b>All savings unlocked</b> &middot; 20% off + free shipping';
    } else if (cad < 3) {
      msg = 'Saving 20% &middot; upgrade to the <b>3-month plan</b> for 40% off';
    } else {
      msg = '<b>All savings unlocked</b> &middot; 40% off + free shipping';
    }

    return { marks: marks, pct: pct, msg: msg, freeHit: freeHit,
             reachedCount: marks.filter(function (m) { return m.on; }).length };
  }

  function renderProgress(cart) {
    if (!cart.item_count) { progEl.hidden = true; progEl.innerHTML = ''; return; }
    var p = buildMilestones(cart);
    progEl.hidden = false;
    progEl.classList.toggle('is-unlocked', p.freeHit);

    var marksHtml = p.marks.map(function (m) {
      return '<span class="cd-prog__mark' + (m.on ? ' is-on' : '') + '" style="left:' + m.pos + '%">'
        + '<span class="cd-prog__dot"></span>'
        + '<span class="cd-prog__lab">' + esc(m.label) + '</span>'
        + '</span>';
    }).join('');

    progEl.innerHTML =
      '<div class="cd-prog__head">'
      +   '<span class="cd-prog__ico" aria-hidden="true">'
      +     (p.freeHit
              ? '<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="m5 13 4 4L19 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
              : '<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')
      +   '</span>'
      +   '<span class="cd-prog__msg">' + p.msg + '</span>'
      + '</div>'
      + '<div class="cd-prog__track">'
      +   '<div class="cd-prog__fill" style="width:' + p.pct + '%"></div>'
      +   '<div class="cd-prog__marks">' + marksHtml + '</div>'
      + '</div>'
      + '<div class="cd-prog__spacer"></div>';

    progEl.dataset.reached = String(p.reachedCount);
  }

  /* ── lines ───────────────────────────────────────────────────── */

  /* The "was" price. A selling-plan line reports the plan price as both its
     original and final in cart.js, so the real compare-at (48.99) lives in the
     cadence config — same source the live drawer trusts. */
  function cadenceForLine(line) {
    var p = planEntry(linePlanId(line));
    return p ? p.entry : null;
  }
  /* Compare-at price of an accessory line (cfg.compareAt), or 0. Goodie bottles
     never use it: their "was" price is the offer's / plan's business. */
  function lineCompareAt(line) {
    if (isGift(line) || linePlanId(line)) return 0;
    if (new Set(cfg.goodieHandles || []).has(String(line.handle || ''))) return 0;
    var each = Number((cfg.compareAt || {})[String(line.variant_id)] || 0);
    return each > Number(line.original_price || line.price || 0) ? each * Number(line.quantity || 1) : 0;
  }
  function lineWas(line) {
    var cad = cadenceForLine(line);
    if (cad && cad.oldPerBottle) return Math.round(Number(cad.oldPerBottle) * 100) * Number(line.quantity || 1);
    var ca = lineCompareAt(line);
    if (ca) return ca;
    return Number(line.original_line_price || line.final_line_price || line.line_price || 0);
  }
  function lineNow(line) {
    return Number(line.final_line_price != null ? line.final_line_price : line.line_price || 0);
  }

  function planText(line) {
    var p = planEntry(linePlanId(line));
    return p ? p.entry.label : '';
  }

  /* One-time lines of an offer, priced as the offer page prices them:
     - pack offers (fermented2): pack price per line (base minus the tier %);
     - free-bottle offers (fermented): the effective price per bottle, i.e.
       what is paid spread over every bottle received (Buy 3 Get 2 -> 29.39).
     Returns null when the cart has no such lines or no deal reached yet. */
  function offerOnceView(cart) {
    var offer = cartOffer(cart);
    if (!offer || !(offer.plans || offer.packs)) return null;
    var lines = offerLines(cart, offer, 'once');
    if (!lines.length) return null;
    var paid = qtyOf(lines);
    if (offer.packs) {
      var pk = packDiscount(cart);
      if (!pk.tier) return null;
      return { tag: offerKey(offer), factor: 1 - Number(pk.tier.pct) / 100 };
    }
    if (freeAppliesTo(offer, 'once')) {
      var free = freeAt(offer, paid);
      if (!free) return null;
      return { tag: offerKey(offer), factor: paid / (paid + free) };
    }
    return null;
  }
  function isOfferOnceLine(line, view) {
    return !!view && !isGift(line) && !linePlanId(line) && lineOffer(line) === view.tag;
  }

  /* Free-bottle offers (fermented): the free Mystery bottles are the deal, so
     the summary shows them as the offer's saving ("Buy 3, get 2 free"), with
     the bottles' full value in the subtotal. Only for offers with their own
     ladder; the BOGO page's summary is untouched. */
  function freeOfferSaving(cart) {
    var offer = cartOffer(cart);
    if (!offer || !offer.plans || !offer.freeBottles) return { value: 0, label: '' };
    var gl = bogoGiftLine(cart);
    var free = gl ? Number(gl.quantity || 0) : 0;
    if (!free) return { value: 0, label: '' };
    var paid = qtyOf(offerLines(cart, offer, 'all'));
    // offers.X.freeLabel names the row itself (benchmark-routine: "1-Month Bottle Free").
    return { value: giftValue(gl), label: offer.freeLabel || ('Buy ' + paid + ', get ' + free + ' free') };
  }

  function renderLine(line, cart) {
    var gift = isGift(line);
    var cur = cart.currency || 'GBP';
    var plan = planText(line);
    var img = line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';
    var wasCents = lineWas(line);
    var nowCents = lineNow(line);
    var ov = offerOnceView(cart);
    if (isOfferOnceLine(line, ov)) {
      var full = Number(line.original_line_price || line.line_price || 0);
      wasCents = full;
      nowCents = Math.round(full * ov.factor);
    }

    var qty = Number(line.quantity || 1);
    return '<div class="cd-line' + (gift ? ' cd-line--gift' : '') + '" data-line-key="' + esc(line.key) + '">'
      + '<div class="cd-line__media">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="96" height="96">' : '')
      + (gift && qty > 1 ? '<span class="cd-line__count">' + qty + '</span>' : '') + '</div>'
      + '<div class="cd-line__main">'
      +   '<div class="cd-line__top">'
      +     '<div><div class="cd-line__title">' + esc(line.product_title || line.title) + '</div>'
      +     (plan ? '<div class="cd-line__meta">' + esc(plan) + '</div>' : '')
      +     (line.variant_title && line.variant_title !== 'Default Title'
              ? '<div class="cd-line__meta">' + esc(line.variant_title) + '</div>' : '')
      +     '</div>'
      +     (gift
              ? '<span class="cd-line__tag">Free</span>'
              : '<button class="cd-line__x" type="button" data-cd-remove aria-label="Remove ' + esc(line.product_title || line.title) + '"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>')
      +   '</div>'
      +   '<div class="cd-line__price">'
      +     (gift && giftValue(line) > 0 ? '<s class="cd-line__was">' + esc(money(giftValue(line), cur)) + '</s> ' : '')
      +     '<span class="cd-line__now">' + (gift ? 'Free' : esc(money(nowCents, cur))) + '</span>'
      +     (!gift && wasCents > nowCents ? '<span class="cd-line__was">' + esc(money(wasCents, cur)) + '</span>' : '')
      +   '</div>'
      +   '<div class="cd-line__bottom">'
      +     (gift ? '<span></span>' :
             '<div class="cd-qty">'
           +   '<button class="cd-qty__btn" type="button" data-cd-qty="-1" aria-label="Decrease quantity">'
           +     '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
           +   '</button>'
           +   '<span class="cd-qty__val">' + Number(line.quantity || 1) + '</span>'
           +   '<button class="cd-qty__btn" type="button" data-cd-qty="1" aria-label="Increase quantity">'
           +     '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
           +   '</button>'
           + '</div>')
      +   '</div>'
      +   subscribeCtaFor(line)
      + '</div>'
      + '</div>';
  }


  /* -- cart proxy ----------------------------------------------- */
  /* POST {items, email, discountCode} -> Shopify App Proxy -> cart.cannumo.io.
     The proxy builds a Storefront cart with the code applied SERVER-SIDE and
     returns a checkoutUrl plus real discounted totals. The Storefront token
     never reaches the browser, and a code cannot be faked client-side because
     Shopify decides whether it applies. */
  var PROXY_ENDPOINT = '/apps/cart/cart';
  var proxy = { inflight: null, hash: null, lastHash: null, result: null };

  function proxyPayload(cart) {
    var items = ((cart && cart.items) || [])
      .filter(function (l) { return !isGift(l); })
      .map(function (l) {
        var plan = linePlanId(l);
        return { variantId: Number(l.variant_id), quantity: Number(l.quantity || 1), sellingPlanId: plan || null };
      });
    return { items: items, email: state.email || null, discountCode: state.discountCode || null };
  }

  async function fetchProxyCart(cart, force) {
    var payload = proxyPayload(cart);
    if (!payload.items.length) return null;
    var hash = JSON.stringify(payload);
    if (!force && proxy.lastHash === hash && proxy.result) return proxy.result;
    if (proxy.inflight && proxy.hash === hash) return proxy.inflight;

    proxy.hash = hash;
    proxy.inflight = (async function () {
      try {
        var res = await fetch(PROXY_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('proxy_' + res.status);
        var data = await res.json();
        proxy.lastHash = hash; proxy.result = data;
        return data;
      } catch (e) {
        proxy.lastHash = null; proxy.result = null;
        return null;
      } finally { proxy.inflight = null; proxy.hash = null; }
    })();
    return proxy.inflight;
  }

  function invalidateProxy() { proxy.lastHash = null; proxy.result = null; }

  /* Mirror to cart attributes so abandoned-cart workflows and the future /cart
     page can read what the customer entered. */
  async function persistAttrs(attrs) {
    try { await postCart('/cart/update.js', { attributes: attrs }); } catch (e) {}
  }

  /* "Offer" cart attribute = the offer tag on the cart's paid lines, so the
     order shows which landing-page offer it was bought under (Order > Additional
     details). Kept in step with the lines on every render and on direct
     checkout, so it cannot outlive a cart that was emptied or rebuilt. */
  async function syncOfferAttr(cart) {
    var tag = '';
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (!isGift(items[i]) && lineOffer(items[i])) { tag = lineOffer(items[i]); break; }
    }
    var cur = String(((cart && cart.attributes) || {}).Offer || '');
    if (cur !== tag) await persistAttrs({ Offer: tag });
  }

  /* Put a code on Shopify's own cart and keep it only if Shopify says it
     applies; otherwise restore whatever code the cart held before. */
  async function applyNativeCode(code) {
    var ship = offerCodesOn(state.cart);
    var before = [cartCode(state.cart) || ''].concat(ship).filter(Boolean).join(',');
    try {
      var c = await postCart('/cart/update.js', { discount: [code].concat(ship).join(',') });
      var hit = ((c && c.discount_codes) || []).find(function (d) { return String(d.code).toLowerCase() === String(code).toLowerCase(); });
      if (hit && hit.applicable) { state.cart = c; return true; }
      await postCart('/cart/update.js', { discount: before });
    } catch (e) {}
    return false;
  }

  /* The code Shopify itself holds on the cart. A /discount/CODE link (emails,
     ads, influencer links) puts it there, not in our discount_code attribute,
     so without this the drawer charged the discounted total but never said why. */
  function cartCode(cart) {
    var list = ((cart && cart.discount_codes) || []).filter(function (d) { return !isPackCode(d.code) && !isOfferCode(d.code); });
    return list.length ? String(list[0].code) : null;
  }

  /* Every discount application in cart.js, cart level and line level. */
  function cartApplications(cart) {
    var apps = ((cart && cart.cart_level_discount_applications) || []).slice();
    ((cart && cart.items) || []).forEach(function (l) {
      (l.line_level_discount_allocations || []).forEach(function (a) { if (a.discount_application) apps.push(a.discount_application); });
    });
    return apps;
  }

  /* Is the applied code a free-shipping code? The proxy knows for certain
     (Admin lookup); without it, Shopify's cart gives it away: the code is
     "applicable" yet takes nothing off any line, because what it discounts
     is delivery, which only exists at checkout. */
  function codeIsFreeShipping(cart) {
    var code = state.discountCode;
    if (!code) return false;
    var pr = proxy.result;
    if (pr && pr.discountApplicable && pr.discountType === 'free_shipping') return true;
    var hit = ((cart && cart.discount_codes) || []).find(function (d) { return String(d.code).toLowerCase() === String(code).toLowerCase(); });
    if (!hit || !hit.applicable) return false;
    return !cartApplications(cart).some(function (a) { return a.type === 'discount_code' && String(a.title).toLowerCase() === String(code).toLowerCase(); });
  }

  /* Pill label from cart.js alone, for when the proxy has no answer: the code's
     own application carries its type and value, at cart level or on a line. */
  function nativeCodeLabel(cart, code) {
    if (codeIsFreeShipping(cart)) return esc(code) + ' (free shipping)';
    var apps = cartApplications(cart), app = null;
    for (var i = 0; i < apps.length; i++) {
      if (apps[i].type === 'discount_code' && String(apps[i].title).toLowerCase() === String(code).toLowerCase()) { app = apps[i]; break; }
    }
    if (!app) return esc(code);
    var val = Number(app.value);
    if (app.value_type === 'percentage' && val > 0) return esc(code) + ' (−' + (+val) + '%)';
    if (app.value_type === 'fixed_amount' && val > 0)
      return esc(code) + ' (−' + money(Math.round(val * 100), (cart && cart.currency) || 'GBP') + ')';
    return esc(code);
  }

  async function applyDiscount(code, btn) {
    var previous = state.discountCode;
    state.discountCode = code;
    invalidateProxy();
    if (btn) { btn.disabled = true; btn.textContent = 'Applying...'; }
    try {
      var result = await fetchProxyCart(state.cart, true);
      if (!result || !result.discountApplicable) {
        /* The proxy said no (or is down). Shopify's own cart is the second
           opinion: it accepts free-shipping codes the Storefront API reports
           as inapplicable (a cart has no delivery cost yet). */
        var native = await applyNativeCode(code);
        if (native) { invalidateProxy(); await persistAttrs({ discount_code: code }); return { ok: true }; }
        state.discountCode = previous; invalidateProxy();
        return { ok: false, msg: result ? 'That code is not valid for this cart' : 'Could not validate that code' };
      }
      await persistAttrs({ discount_code: code });
      // Shopify's cart carries the code as well, so cart.js totals, the
      // checkout and a later page load all agree with the drawer.
      try { await postCart('/cart/update.js', { discount: [code].concat(offerCodesOn(state.cart)).join(',') }); } catch (e) {}
      return { ok: true };
    } finally { if (btn) { btn.disabled = false; btn.textContent = 'Apply'; } }
  }

  /* -- confetti ------------------------------------------------- */
  /* Fires once when a milestone is newly reached. Self-contained canvas, no
     library, and it does nothing at all under prefers-reduced-motion. */
  var reachedBefore = null;
  function celebrate(origin) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var c = document.createElement('canvas');
    c.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:10000';
    document.body.appendChild(c);
    var ctx = c.getContext('2d'), dpr = window.devicePixelRatio || 1;
    c.width = innerWidth * dpr; c.height = innerHeight * dpr;
    c.style.width = innerWidth + 'px'; c.style.height = innerHeight + 'px';
    ctx.scale(dpr, dpr);

    var cs = getComputedStyle(document.documentElement);
    var colours = ['#d78a00', (cs.getPropertyValue('--about-stat-bg') || '#efe3d8').trim(), '#111111', '#ffffff'];
    var parts = [];
    for (var i = 0; i < 70; i++) {
      var a = (Math.PI * 2 * i) / 70 + Math.random() * 0.3;
      var sp = 3 + Math.random() * 5;
      parts.push({ x: origin.x, y: origin.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2,
        r: 2 + Math.random() * 3, col: colours[i % colours.length], rot: Math.random() * 6, life: 1 });
    }
    var t0 = performance.now();
    (function frame(t) {
      var dt = Math.min(32, t - t0); t0 = t;
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      var alive = false;
      parts.forEach(function (pt) {
        if (pt.life <= 0) return;
        alive = true;
        pt.vy += 0.16 * (dt / 16); pt.x += pt.vx * (dt / 16); pt.y += pt.vy * (dt / 16);
        pt.life -= 0.012 * (dt / 16); pt.rot += 0.2;
        ctx.globalAlpha = Math.max(0, pt.life);
        ctx.fillStyle = pt.col;
        ctx.save(); ctx.translate(pt.x, pt.y); ctx.rotate(pt.rot);
        ctx.fillRect(-pt.r, -pt.r * 0.6, pt.r * 2, pt.r * 1.2);
        ctx.restore();
      });
      if (alive) requestAnimationFrame(frame); else c.remove();
    })(t0);
  }

  function checkUnlock() {
    var track = progEl.querySelector('.cd-prog__track');
    var reached = Number(progEl.dataset.reached || 0);
    if (reachedBefore !== null && reached > reachedBefore && track) {
      var r = track.getBoundingClientRect();
      celebrate({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    }
    reachedBefore = reached;
  }


  /* -- Orphan-gift guard ---------------------------------------- */
  /* Non-gift ("normal", paid) lines in the cart. */
  function normalLines(cart) {
    return ((cart && cart.items) || []).filter(function (l) { return !isGift(l); });
  }
  /* When the last paid item is removed, no free gift may remain — otherwise the
     cart is a gifts-only cart that could reach checkout. syncBogo only manages
     the BOGO bottle; this clears every gift line (freebies, pin, BOGO). Matches
     the live drawer treating "no normal lines" as an empty cart. */
  async function stripOrphanGifts(cart) {
    if (normalLines(cart).length > 0) return false;
    var gifts = ((cart && cart.items) || []).filter(isGift);
    if (!gifts.length) return false;
    for (var i = 0; i < gifts.length; i++) {
      await postCart('/cart/change.js', { id: gifts[i].key, quantity: 0 });
    }
    return true;
  }

  /* -- Subscription freebie sync (G4/G5) ------------------------ */
  /* The two subscription freebies (recipe PDF + Mystery Goodie 60ml) follow the
     CART, never the page: present iff a subscription line exists AND no offer
     that ships its own gifts (A/B new-plan) has taken over. Without this the
     freebies stay stranded when a customer switches a subscription to a
     one-time purchase — the exact "replace/remove on switch" gap. Ported from
     the live drawer's syncFreeGifts. */
  var subGifts = { syncing: false };
  /* Every gift line this sync OWNS: the base freebies + the 90-day-only tier. */
  function managedGiftIds() {
    return [].concat((cfg.freeGiftVariantIds || []).map(Number), (cfg.planGiftTier90 || []).map(Number),
                     (cfg.retiredGiftVariantIds || []).map(Number));   // managed so they get stripped, never wanted
  }
  function subFreebieLines(cart) {
    var ids = new Set(managedGiftIds());
    return ((cart && cart.items) || []).filter(function (l) { return ids.has(Number(l.variant_id)); });
  }
  async function syncFreeGifts(cart) {
    if (subGifts.syncing) return false;
    subGifts.syncing = true;
    try {
      var base = (cfg.freeGiftVariantIds || []).map(Number);
      var tier90 = (cfg.planGiftTier90 || []).map(Number);
      // Wanted set follows the cart: base freebies (e-book + Mystery Goodie 60ml) on any
      // subscription — goodie-e 30/90-day AND BOGO subscriptions. The tier-90 gift
      // (pin) is a goodie-e 90-day perk only, so it also requires the plan-gift
      // offer that goodie-e tags — a BOGO 90-day cart (same plan id, untagged)
      // must NOT get the pin.
      var wantIds = [];
      if (hasSubscription(cart) && !cartHasNewPlan(cart)) {
        wantIds = base.slice();
        if (cadenceOf(cart) >= 3 && cartHasPlanGift(cart)) wantIds = wantIds.concat(tier90);
        /* Anything this cart's offer grants beyond the defaults. Listed in
           retiredGiftVariantIds it is still managed, so it is rendered and
           priced as a gift; naming it here is what stops it being stripped. */
        var offer = cartOffer(cart);
        if (offer && offer.gifts) wantIds = wantIds.concat(offer.gifts.map(Number));
        // giftsPlans (benchmark-routine): the gifts come with these plans only.
        if (offer && offer.giftsPlans && !((cart.items) || []).some(function (l) {
          return !isGift(l) && offer.giftsPlans.indexOf(linePlanId(l)) > -1;
        })) wantIds = [];
      }
      /* Simple offers replace the defaults entirely: no default e-book, only
         the offer's own gifts (42b's rewards), and only with its subscription. */
      var so = simpleOffer(cart);
      if (so) wantIds = simpleSubscribed(cart, so) ? (so.gifts || []).map(Number) : [];
      var wantSet = new Set(wantIds);
      var managed = subFreebieLines(cart);
      var mutated = false;
      // Strip any managed gift that is no longer wanted (e.g. pin after a 90→30 downgrade).
      for (var i = 0; i < managed.length; i++) {
        if (!wantSet.has(Number(managed[i].variant_id))) { await postCart('/cart/change.js', { id: managed[i].key, quantity: 0 }); mutated = true; }
      }
      // Add any wanted gift not already present.
      var have = new Set(managed.map(function (l) { return Number(l.variant_id); }));
      var toAdd = wantIds.filter(function (id) { return !have.has(id); });
      if (toAdd.length) { await postCart('/cart/add.js', { items: toAdd.map(function (id) { return { id: id, quantity: 1 }; }) }); mutated = true; }
      return mutated;
    } catch (e) {
      return false;                                         // never block rendering
    } finally { subGifts.syncing = false; }
  }

  /* -- BOGO sync ------------------------------------------------ */
  /* Ported from the live drawer's syncBogo. The entitlement test reads the
     CART, never the page: someone who earned a gift on a BOGO page keeps it
     while browsing elsewhere. Getting this backwards silently deletes a gift
     the customer earned, which is why the opt-out is expressed as "the cart
     says no", not "this page isn't a BOGO page".

     Opt-outs, both cart-based:
       1. an A/B subscription line (newPlanIds) — those carts opt out entirely
       2. a v3-3pack line (properties._offer) — that offer ships its own gifts
          (PDF + Mystery Goodie 60ml + pin), so BOGO must not stack on top.

     b2g2f templates need 2 triggers to earn one bottle; everywhere else 1. */
  var bogo = { syncing: false, paused: false };

  function bogoTriggerQty(cart) {
    var handles = new Set(cfg.goodieHandles || []);
    return ((cart && cart.items) || []).reduce(function (sum, l) {
      if (isGift(l)) return sum;
      var handle = String((l.handle || (l.url || '').split('/').pop() || '')).split('?')[0];
      return handles.has(handle) ? sum + Number(l.quantity || 0) : sum;
    }, 0);
  }

  /* Free bottles for an offer that defines freeBottles: count the paid
     subscription bottles carrying that offer's tag and read the table, using
     its top row for anything larger. One-time bottles earn nothing unless the
     offer sets oneTime: true, in which case they count the same. freeOn:
     'once' is the reverse: only one-time bottles earn (fermented, whose
     subscriptions sell extra paid bottles instead of free ones). */
  function offerFreeBottles(cart, offer) {
    var tag = '';
    var defs = cfg.offers || {};
    Object.keys(defs).forEach(function (k) { if (defs[k] === offer) tag = k; });
    var paid = ((cart && cart.items) || []).reduce(function (sum, l) {
      if (isGift(l) || lineOffer(l) !== tag) return sum;
      // freePlans (benchmark-routine): only bottles on these plans earn free ones.
      if (offer.freePlans && linePlanId(l) && offer.freePlans.indexOf(linePlanId(l)) < 0) return sum;
      if (offer.freeOn === 'once') { if (linePlanId(l)) return sum; }
      else if (!linePlanId(l) && !offer.oneTime) return sum;
      return sum + Number(l.quantity || 0);
    }, 0);
    var table = offer.freeBottles;
    var keys = Object.keys(table).map(Number).sort(function (a, b) { return a - b; });
    if (!paid || !keys.length) return 0;
    var key = paid > keys[keys.length - 1] ? keys[keys.length - 1] : paid;
    return Number(table[key] || 0);
  }

  function bogoGiftLine(cart) {
    var ids = new Set((cfg.bogoGiftVariantIds || []).map(Number));
    return ((cart && cart.items) || []).find(function (l) { return ids.has(Number(l.variant_id)); }) || null;
  }

  async function syncBogo(cart) {
    if (bogo.syncing || bogo.paused) return false;
    bogo.syncing = true;
    try {
      var giftLine = bogoGiftLine(cart);
      var giftId = Number((cfg.bogoGiftVariantIds || [])[0]);
      if (!giftId) return false;

      // Cart-based opt-out — never page-based.
      if (cartOptsOutOfBogo(cart)) {
        if (giftLine) { await postCart('/cart/change.js', { id: giftLine.key, quantity: 0 }); return true; }
        return false;
      }

      var isB2G2F = window.CD_PAGE_TEMPLATE === 'page.goodiev3-b2g2f'
        || location.pathname.indexOf('/pages/goodie-b2g2f') > -1;
      var minQty = isB2G2F ? 2 : 1;

      var triggers = bogoTriggerQty(cart);
      var earned = triggers >= minQty ? Math.floor(triggers / minQty) : 0;
      // An offer with its own free-bottle table (e.g. "fermented": 2 -> 1,
      // 3 -> 2) replaces the 1:1 count. Its subscription lines earn (and its
      // one-time lines too when the offer sets oneTime).
      var tableOffer = cartOffer(cart);
      if (tableOffer && tableOffer.freeBottles) earned = offerFreeBottles(cart, tableOffer);
      // bogoSubOnly (benchmark): only the offer's own subscription bottles earn
      // the free one; its one-time purchase gets none.
      if (tableOffer && tableOffer.bogoSubOnly) earned = qtyOf(offerLines(cart, tableOffer, 'sub'));
      // An offer may cap the matched free bottles (goodiev41no3b: Buy 1 Get 1 only).
      if (tableOffer && tableOffer.bogoMax != null) earned = Math.min(earned, Number(tableOffer.bogoMax));
      var have = giftLine ? Number(giftLine.quantity || 0) : 0;

      if (earned === have) return false;
      if (earned === 0) {
        if (giftLine) { await postCart('/cart/change.js', { id: giftLine.key, quantity: 0 }); return true; }
        return false;
      }
      if (giftLine) await postCart('/cart/change.js', { id: giftLine.key, quantity: earned });
      else await postCart('/cart/add.js', { items: [{ id: giftId, quantity: earned }] });
      return true;
    } catch (e) {
      // A failed gift sync must never block the drawer from rendering.
      return false;
    } finally { bogo.syncing = false; }
  }

  window.CannumoBogo = {
    sync: function () { return syncBogo(state.cart); },
    pause: function () { bogo.paused = true; },
    resume: function () { bogo.paused = false; }
  };

  /* ── grouping ────────────────────────────────────────────────── */
  /* The v3 PDP pushes one line PER BOTTLE so flavours can be mixed
     (items.push({id, quantity:1, properties:{_offer}}) in a loop). Showing
     three near-identical rows reads as a mistake, so sibling lines sharing a
     plan collapse into one bundle card with a slot per bottle — the cart-side
     twin of the PDP's "mix your three bottles". */
  function groupLines(cart) {
    var items = ((cart && cart.items) || []).filter(function (l) { return !isGift(l); });
    var bundles = {}, singles = [];
    /* Offers with their own ladder (fermented, fermented2) also group their
       ONE-TIME bottles into one pack card, like a subscription bundle. Every
       other cart keeps one-time lines as single rows. */
    var offerG = cartOffer(cart);
    var onceTag = offerG && (offerG.plans || offerG.packs) ? offerKey(offerG) : '';

    items.forEach(function (l) {
      var plan = linePlanId(l);
      if (!plan && onceTag && lineOffer(l) === onceTag) {
        var ok = 'once|' + onceTag;
        if (!bundles[ok]) bundles[ok] = { key: ok, plan: '', once: true, offer: onceTag, lines: [], slots: [] };
        bundles[ok].lines.push(l);
        for (var j = 0; j < Number(l.quantity || 1); j++) {
          bundles[ok].slots.push({ lineKey: l.key, variantId: Number(l.variant_id), title: l.product_title || l.title });
        }
        return;
      }
      if (!plan) { singles.push(l); return; }
      var key = plan + '|' + lineOffer(l);
      if (!bundles[key]) bundles[key] = { key: key, plan: plan, offer: lineOffer(l), lines: [], slots: [] };
      bundles[key].lines.push(l);
      // A line with qty 3 is three slots of the same flavour.
      for (var i = 0; i < Number(l.quantity || 1); i++) {
        bundles[key].slots.push({ lineKey: l.key, variantId: Number(l.variant_id), title: l.product_title || l.title });
      }
    });

    return { bundles: Object.keys(bundles).map(function (k) { return bundles[k]; }), singles: singles };
  }

  /* The flavours a bundle's slots can switch between: the offer's own
     (simple offers sell their -offer products), otherwise the shared list. */
  function flavourList(tag) {
    var o = tag ? (cfg.offers || {})[tag] : null;
    return (o && o.flavours) || cfg.flavours || [];
  }
  function flavourFor(variantId, title) {
    var hit = (cfg.flavours || []).find(function (f) { return Number(f.variantId) === Number(variantId); });
    if (hit) return hit;
    var defsF = cfg.offers || {};
    Object.keys(defsF).some(function (k) {
      hit = (defsF[k].flavours || []).find(function (f) { return Number(f.variantId) === Number(variantId); });
      return !!hit;
    });
    if (hit) return hit;
    var t = String(title || '').toLowerCase();
    return (cfg.flavours || []).find(function (f) { return t.indexOf(f.name.toLowerCase()) > -1; })
      || { name: title || 'Goodie', dot: 'transparent' };
  }

  function cadenceKeyForPlan(plan) {
    var p = planEntry(plan);
    return p ? p.key : undefined;
  }
  /* The plans a bundle can switch between: its offer's own table when the
     bundle is on one of those plans, otherwise the shared cadences. A bundle
     can never be switched onto another offer's plans. */
  function tableForBundle(bundle) {
    var p = planEntry(bundle.plan);
    return p ? p.table : cfg.cadences;
  }

  /* Cadence menu — a real popover, not a native select, because each option
     carries its own discount pill and the list ends with a one-time escape
     hatch. v3-3pack hides the 2-month step, matching the PDP and progress bar. */
  function cadenceMenu(bundle, cadKey) {
    var offerDef = (cfg.offers || {})[bundle.offer] || {};
    var allowed = (offerDef.cadences || []).map(Number);
    var table = tableForBundle(bundle);
    var shared = table === cfg.cadences;
    var opts = Object.keys(table).filter(function (k) {
      if (!shared) return true;
      if (bundle.offer === cfg.v3OfferTag && Number(k) === 2) return false;
      // noUpgrade offers: nothing larger than the plan the cart is on.
      if (offerDef.noUpgrade && Number(k) > Number(cadKey)) return false;
      // Retired cadences (the 2-month plan) only for offers that list them.
      return !cfg.cadences[k].retired || allowed.indexOf(Number(k)) > -1 || String(k) === String(cadKey);
    }).map(function (k) {
      var c = table[k];
      var on = String(k) === String(cadKey);
      /* Offer plans: the real saving and price per bottle, free bottles
         included, so the options read differently (fermented's plans all
         carry -20% on the paid bottles). Shared plans keep their pill. */
      if (!shared) {
        var effM = subEffective(offerDef, c);
        return '<button class="cd-cad__opt' + (on ? ' is-current' : '') + '" type="button" role="menuitemradio" aria-checked="' + on + '"'
          + ' data-cd-cadence="' + Number(k) + '" data-cd-bundle="' + esc(bundle.key) + '">'
          + '<span>' + esc(c.cadenceLabel) + '</span>'
          + '<span class="cd-cad__optend">'
          +   (effM.each ? '<span class="cd-cad__optprice">' + esc(money(effM.each, (state.cart && state.cart.currency) || 'GBP')) + '/bottle</span>' : '')
          +   (effM.pct ? '<span class="cd-cad__optpct">−' + effM.pct + '%</span>' : '')
          + '</span></button>';
      }
      return '<button class="cd-cad__opt' + (on ? ' is-current' : '') + '" type="button" role="menuitemradio" aria-checked="' + on + '"'
        + ' data-cd-cadence="' + Number(k) + '" data-cd-bundle="' + esc(bundle.key) + '">'
        + '<span>' + esc(c.cadenceLabel) + '</span>'
        + (c.pct ? '<span class="cd-cad__optpct">−' + Number(c.pct) + '%</span>' : '')
        + '</button>';
    }).join('');
    return '<div class="cd-cad__menu" role="menu" hidden>'
      + opts
      + '<button class="cd-cad__opt cd-cad__opt--onetime" type="button" role="menuitem" data-cd-onetime data-cd-bundle="' + esc(bundle.key) + '">Switch to one-time purchase</button>'
      + '</div>';
  }

  function closeCadMenus() {
    root.querySelectorAll('.cd-cad__menu').forEach(function (m) { m.hidden = true; });
    root.querySelectorAll('[data-cd-cad-toggle]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
  }

  /* One-time: keep the bottles and flavours, drop the selling plan. Properties
     carry across so a v3-3pack cart stays tagged and BOGO still stays off it. */
  function switchToOneTime(bkey) {
    act(async function () {
      var g = groupLines(state.cart);
      var bundle = g.bundles.find(function (b) { return b.key === bkey; });
      if (!bundle) return;
      var props = Object.assign({}, (bundle.lines[0] && bundle.lines[0].properties) || {});
      /* An offer bundle lands on the matching one-time option (offers.X.onceFor:
         plan key -> paid bottles), e.g. fermented's 5-bottle plan becomes 3 paid
         + 2 free, and pack offers mark the lines for the pack price. */
      var pe = planEntry(bundle.plan);
      var offerDef = pe && pe.offer ? (cfg.offers || {})[pe.offer] : null;
      var keep = bundle.slots;
      if (offerDef && offerDef.onceFor && offerDef.onceFor[pe.key]) keep = bundle.slots.slice(0, Number(offerDef.onceFor[pe.key]));
      if (offerDef && offerDef.packs) props._pack = 'otp';
      var counts = {};
      keep.forEach(function (s) { counts[s.variantId] = (counts[s.variantId] || 0) + 1; });
      var items = Object.keys(counts).map(function (v) {
        return { id: Number(v), quantity: counts[v], properties: Object.assign({}, props) };
      });
      await removeLines(bundle.lines);
      await postCart('/cart/add.js', { items: items });
    });
  }

  /* Switching cadence changes bottle COUNT, not just the plan: the 2-month plan
     is 2 bottles, the 3-month is 3, the monthly is 1. So reconcile the slots to
     the target cadence's qty — keep the flavours already chosen, and if more
     slots are needed, extend by repeating the last chosen flavour. */
  function switchCadence(bkey, targetKey) {
    var pre = groupLines(state.cart).bundles.find(function (b) { return b.key === bkey; });
    if (!pre || !tableForBundle(pre)[targetKey]) return;
    act(async function () {
      var g = groupLines(state.cart);
      var bundle = g.bundles.find(function (b) { return b.key === bkey; });
      if (!bundle) return;
      var table = tableForBundle(bundle);
      var target = table[targetKey];
      if (!target) return;
      var shared = table === cfg.cadences;

      var chosen = bundle.slots.map(function (s) { return Number(s.variantId); });
      var wantQty = Number(target.qty || chosen.length);
      var props = Object.assign({}, (bundle.lines[0] && bundle.lines[0].properties) || {});
      delete props._pack;   // subscription lines never take the one-time pack price

      /* Shared plans extend by repeating the last flavour, as they always have.
         Offer plans fill new slots from the preselect cycle (3 = one of each,
         6 = two of each), so a step up reads like the page's own options. */
      var cycle = flavourCycle();
      var variants = [];
      for (var i = 0; i < wantQty; i++) {
        if (chosen[i] != null) variants.push(chosen[i]);
        else if (!shared && cycle.length) variants.push(cycle[i % cycle.length]);
        else variants.push(chosen[chosen.length - 1] || chosen[0]);
      }
      // Collapse to one add-line per variant with a quantity.
      var counts = {};
      variants.forEach(function (v) { counts[v] = (counts[v] || 0) + 1; });
      var items = Object.keys(counts).map(function (v) {
        return { id: Number(v), quantity: counts[v], selling_plan: Number(target.plan), properties: Object.assign({}, props) };
      });

      await removeLines(bundle.lines);
      await postCart('/cart/add.js', { items: items });
    });
  }

  /* One-time pack card for fermented / fermented2: the offer's one-time bottles
     as one item, priced like the page (fermented: paid total against every
     bottle received at full price; fermented2: the pack price), flavour slots
     to swap, one remove, and one upgrade: Subscribe & save to the matching
     subscription option. Same classes as the subscription bundle. */
  function renderOnceBundle(bundle, cart) {
    var cur = cart.currency || 'GBP';
    var offer = (cfg.offers || {})[bundle.offer] || {};
    var first = bundle.lines[0];
    var img = first.image ? first.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';
    var paid = bundle.slots.length;
    var base = bundle.lines.reduce(function (a, l) { return a + Number(l.original_line_price || l.line_price || 0); }, 0);
    var now = base, was = base, free = 0, title, meta;
    if (offer.packs) {
      var pk = packDiscount(cart);
      if (pk.tier) now = base - pk.expected;
      title = 'Buy ' + paid;
      meta = paid + ' bottle' + (paid === 1 ? '' : 's') + (pk.tier ? ' &middot; pack price, save ' + Math.round(Number(pk.tier.pct)) + '%' : '') + ' &middot; one-time';
    } else if (offer.simple) {
      // Beside the subscription these are one-off extras at full price.
      var subbedO = simpleSubscribed(cart, offer);
      title = subbedO ? 'One-off bottle' + (paid === 1 ? '' : 's') : 'One-time purchase';
      meta = paid + ' bottle' + (paid === 1 ? '' : 's') + ' &middot; ' + (subbedO ? 'full price, not part of your plan' : 'no subscription');
    } else {
      free = freeAppliesTo(offer, 'once') ? freeAt(offer, paid) : 0;
      if (free) was = Math.round(base / paid) * (paid + free);
      title = free ? 'Buy ' + paid + ', Get ' + free + ' Free' : 'Buy ' + paid;
      meta = paid + ' bottle' + (paid === 1 ? '' : 's') + (free ? ' + ' + free + ' free Mystery' : '') + ' &middot; one-time';
    }

    var slots = bundle.slots.map(function (s, i) {
      var f = flavourFor(s.variantId, s.title);
      var opts = flavourList(bundle.offer).filter(function (o) { return Number(o.variantId) > 0; }).map(function (o) {
        return '<option value="' + Number(o.variantId) + '"' + (Number(o.variantId) === Number(s.variantId) ? ' selected' : '') + '>' + esc(o.name) + '</option>';
      }).join('');
      return '<div class="cd-slot" data-slot-line="' + esc(s.lineKey) + '" data-slot-variant="' + Number(s.variantId) + '">'
        + '<span class="cd-slot__num">' + (i + 1) + '</span>'
        + '<span class="cd-slot__dot" style="background:' + esc(f.dot) + '"></span>'
        + '<span class="cd-slot__flav">' + esc(f.name) + '</span>'
        + '<svg class="cd-slot__chev" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + (opts ? '<select class="cd-slot__select" data-cd-slot aria-label="Change flavour for bottle ' + (i + 1) + '">' + opts + '</select>' : '')
        + '</div>';
    }).join('');

    var tgt = offerSubTarget(cart, offer);
    // A simple offer already subscribed keeps one-off extras one-off.
    if (offer.simple && simpleSubscribed(cart, offer)) tgt = null;
    var upgrade = '';
    if (tgt) {
      var eff = subEffective(offer, tgt.entry);
      var subLine = esc(tgt.entry.label || '') + (eff.each ? ' &middot; ' + esc(money(eff.each, cur)) + '/bottle' : '');
      // Simple offers: the first-order price and what else subscribing brings.
      if (offer.simple) subLine = (eff.each ? esc(money(eff.each, cur)) + (paid > 1 ? ' each' : '') + ' first order &middot; ' : '') + esc(offer.upPitch || 'free shipping');
      upgrade = '<button class="cd-bundle__upgrade" type="button" data-cd-subscribe-offer>'
        + '<svg class="cd-up__ico" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span class="cd-up__text">Subscribe &amp; save'
        +   '<span class="cd-up__sub">' + subLine + '</span></span>'
        + (eff.pct ? '<span class="cd-up__save">−' + eff.pct + '%</span>' : '')
        + '<svg class="cd-up__chev" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '</button>';
    }

    return '<div class="cd-bundle cd-bundle--once" data-bundle-key="' + esc(bundle.key) + '">'
      + '<div class="cd-bundle__head">'
      +   '<div class="cd-bundle__media">'
      +     (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="64" height="64">' : '')
      +     '<span class="cd-bundle__count">' + (paid + free) + '</span>'
      +   '</div>'
      +   '<div class="cd-bundle__info">'
      +     '<div class="cd-line__title">' + esc(title) + '</div>'
      +     '<div class="cd-line__meta">' + meta + '</div>'
      +     '<div class="cd-line__price">'
      +       '<span class="cd-line__now">' + esc(money(now, cur)) + '</span>'
      +       (was > now ? '<span class="cd-line__was">' + esc(money(was, cur)) + '</span>' : '')
      +     '</div>'
      +   '</div>'
      +   '<button class="cd-line__x cd-bundle__x" type="button" data-cd-remove-bundle="' + esc(bundle.key) + '" aria-label="Remove"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>'
      + '</div>'
      + '<div class="cd-slots"><div class="cd-slots__hdr">Your bottles<span>tap to swap</span></div>' + slots + '</div>'
      + upgrade
      + '</div>';
  }

  function renderBundle(bundle, cart) {
    if (bundle.once) return renderOnceBundle(bundle, cart);
    var cur = cart.currency || 'GBP';
    var table = tableForBundle(bundle);
    var cadKey = cadenceKeyForPlan(bundle.plan);
    var cad = cadKey ? table[cadKey] : null;
    var first = bundle.lines[0];
    var img = first.image ? first.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';

    var now = bundle.lines.reduce(function (a, l) { return a + lineNow(l); }, 0);
    var was = bundle.lines.reduce(function (a, l) { return a + lineWas(l); }, 0);
    var bottles = bundle.slots.length;
    /* The plan's headline discount comes from config, not from rounding was vs
       now — 97.98 → 67.98 is 30.6%, which rounded to a stray 31%. The plan is a
       flat 30%, so show that. */
    var savePct = cad ? Number(cad.pct || 0) : 0;
    var peS = planEntry(bundle.plan);
    if (cad && peS && peS.offer) savePct = subEffective((cfg.offers || {})[peS.offer], cad).pct;   // offer plans: real saving incl. free bottles
    // Offers whose subscriptions carry free bottles every delivery (fermented).
    var peB = planEntry(bundle.plan);
    var offB = peB && peB.offer ? (cfg.offers || {})[peB.offer] : null;
    var bundleFree = offB && freeAppliesTo(offB, 'sub') ? freeAt(offB, bottles) : 0;

    var slots = bundle.slots.map(function (s, i) {
      var f = flavourFor(s.variantId, s.title);
      var opts = flavourList(bundle.offer).filter(function (o) { return Number(o.variantId) > 0; }).map(function (o) {
        return '<option value="' + Number(o.variantId) + '"' + (Number(o.variantId) === Number(s.variantId) ? ' selected' : '') + '>' + esc(o.name) + '</option>';
      }).join('');
      return '<div class="cd-slot" data-slot-line="' + esc(s.lineKey) + '" data-slot-variant="' + Number(s.variantId) + '">'
        + '<span class="cd-slot__num">' + (i + 1) + '</span>'
        + '<span class="cd-slot__dot" style="background:' + esc(f.dot) + '"></span>'
        + '<span class="cd-slot__flav">' + esc(f.name) + '</span>'
        + '<svg class="cd-slot__chev" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + (opts ? '<select class="cd-slot__select" data-cd-slot aria-label="Change flavour for bottle ' + (i + 1) + '">' + opts + '</select>' : '')
        + '</div>';
    }).join('');

    // Upgrade nudge to the next cadence. A two-line green affordance with the
    // extra-bottles benefit and the plan's discount pill, matching the
    // prototype — the saving is the plan's, never cart value.
    var upgrade = '';
    var nextKey = null;
    if (table !== cfg.cadences) {
      // Offer plans: the next larger plan in the offer's own table.
      nextKey = Object.keys(table).map(Number).sort(function (a, b) { return a - b; })
        .find(function (k) { return k > Number(cadKey); }) || null;
    } else if (cadKey && Number(cadKey) < 3 && !((cfg.offers || {})[bundle.offer] || {}).noUpgrade) {
      // (offers.X.noUpgrade: the offer sells one plan only, so no step up.)
      nextKey = Number(cadKey) + 1;
      // The 2-month plan is discontinued — the step after monthly is the 3-month plan.
      if (nextKey === 2) nextKey = 3;
    }
    if (nextKey) {
      var next = table[nextKey];
      if (next) {
        var extra = Number(next.qty || 0) - bottles;
        /* An offer that gives free bottles on subscriptions (fermented) sells
           the step up by the free bottles it adds, not by a plan percentage. */
        var peU = planEntry(bundle.plan);
        var offU = peU && peU.offer ? (cfg.offers || {})[peU.offer] : null;
        var freeNext = offU && freeAppliesTo(offU, 'sub') ? freeAt(offU, Number(next.qty || 0)) : 0;
        var freeGain = freeNext - (offU && freeAppliesTo(offU, 'sub') ? freeAt(offU, bottles) : 0);
        var sub = (extra > 0 ? '+' + extra + ' more bottle' + (extra === 1 ? '' : 's') : 'more bottles')
                + (freeGain > 0 ? ' &middot; ' + freeNext + ' free Mystery bottle' + (freeNext === 1 ? '' : 's') + ' every delivery'
                   : (next.pct ? ' &middot; save ' + Number(next.pct) + '% every bottle' : ''));
        /* Offer plans: one fact per part, nothing repeated. Title = the plan,
           line = what changes (bottles added and free bottles, or bottles and
           price each), pill = the real saving per bottle. */
        var titleTxt = esc(next.cadenceLabel.toLowerCase()) + ' plan', pill = next.pct ? '−' + Number(next.pct) + '%' : '';
        if (table !== cfg.cadences) {
          var effU = subEffective(offU || {}, next);
          titleTxt = planName(nextKey, next);
          pill = effU.pct ? '−' + effU.pct + '%' : '';
          var nb = function (n) { return n + ' bottle' + (n === 1 ? '' : 's'); };
          sub = freeGain > 0
            ? 'Add ' + nb(extra) + ', get ' + freeNext + ' free Mystery bottle' + (freeNext === 1 ? '' : 's')
            : nb(Number(next.qty || 0)) + ' per delivery' + (effU.each ? ', ' + esc(money(effU.each, cur)) + ' each' : '');
        }
        upgrade = '<button class="cd-bundle__upgrade" type="button" data-cd-cadence="' + nextKey + '" data-cd-bundle="' + esc(bundle.key) + '">'
          + '<svg class="cd-up__ico" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          + '<span class="cd-up__text">Upgrade to <b>' + titleTxt + '</b>'
          +   '<span class="cd-up__sub">' + sub + '</span></span>'
          + (pill ? '<span class="cd-up__save">' + pill + '</span>' : '')
          + '<svg class="cd-up__chev" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          + '</button>';
      }
    }

    return '<div class="cd-bundle" data-bundle-key="' + esc(bundle.key) + '">'
      + '<div class="cd-bundle__head">'
      +   '<div class="cd-bundle__media">'
      +     (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="64" height="64">' : '')
      +     ((bundle.slots.length + bundleFree) > 1 ? '<span class="cd-bundle__count">' + (bundle.slots.length + bundleFree) + '</span>' : '')
      +   '</div>'
      +   '<div class="cd-bundle__info">'
      +     '<div class="cd-line__title">Goodie Subscription</div>'
      +     '<div class="cd-line__meta">' + bottles + ' bottle' + (bottles === 1 ? '' : 's') + (bundleFree > 0 ? ' + ' + bundleFree + ' free' : '') + ' per delivery'
      +       (offB && offB.simple && (cfg.planThen || {})[bundle.plan] ? ' &middot; then ' + esc(money(Number(cfg.planThen[bundle.plan]) * bottles, cur)) + '/month' : '') + '</div>'
      +     '<div class="cd-line__price">'
      +       '<span class="cd-line__now">' + esc(money(now, cur)) + '</span>'
      +       (was > now ? '<span class="cd-line__was">' + esc(money(was, cur)) + '</span>' : '')
      +     '</div>'
      +   '</div>'
      +   '<button class="cd-line__x cd-bundle__x" type="button" data-cd-remove-bundle="' + esc(bundle.key) + '" aria-label="Remove subscription"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>'
      + '</div>'
      /* Cadence chip: frequency plus the plan's own discount %, computed from
         was vs now rather than hardcoded — the number is whatever the plan
         actually saves on this cart. Chevron opens a native select to switch. */
      + (cad
          ? '<div class="cd-cad" data-cd-cad>'
          +   '<button class="cd-cad__trigger" type="button" data-cd-cad-toggle aria-haspopup="true" aria-expanded="false">'
          +     '<svg class="cd-cad__ico" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          +     '<span class="cd-cad__txt">' + esc(cad.label) + '</span>'
          +     (savePct > 0 ? '<span class="cd-cad__pct">−' + savePct + '%</span>' : '')
          +     '<svg class="cd-cad__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          +   '</button>'
          +   cadenceMenu(bundle, cadKey)
          + '</div>'
          : '')
      + (slots ? '<div class="cd-slots"><div class="cd-slots__hdr">Your bottles<span>tap to swap</span></div>' + slots + '</div>' : '')
      + upgrade
      + '</div>';
  }

  /* One-time Goodie lines had no route into a subscription. The Figma frame
     shows an upgrade CTA and the funnels depend on it, so offer the entry
     cadence and let the customer step up from there. */
  /* Sits INSIDE the line it applies to, not after the list — the prototype puts
     it under the product it upgrades, which is where the decision is made. */
  /* Offer carts subscribe as a whole: the one-time bottles map to the offer's
     matching subscription option (offers.X.subFor: paid bottles -> plan key,
     largest row not above the count), never to the shared monthly plan. */
  function offerSubTarget(cart, offer) {
    if (!offer || !offer.plans || !offer.subFor) return null;
    var paid = qtyOf(offerLines(cart, offer, 'once'));
    if (!paid) return null;
    var key = null;
    Object.keys(offer.subFor).map(Number).sort(function (a, b) { return a - b; }).forEach(function (n) {
      if (paid >= n) key = offer.subFor[n];
    });
    return key != null && offer.plans[key] ? { key: key, entry: offer.plans[key] } : null;
  }

  /* Price per bottle of an offer subscription option as the page shows it:
     the plan price spread over every bottle a delivery brings, free ones
     included (3 + 2 free at 39.19 -> 23.51), and its saving against 48.99. */
  function subEffective(offer, entry) {
    var perB = planPerBottle(entry.plan);
    var qty = Number(entry.qty || 1);
    var free = freeAppliesTo(offer, 'sub') ? freeAt(offer, qty) : 0;
    var each = perB ? Math.round(perB * qty / (qty + free)) : 0;
    var full = Math.round(Number(entry.oldPerBottle || 0) * 100);
    var pct = each && full ? Math.round((1 - each / full) * 100) : Number(entry.pct || 0);
    // Simple offers are sold at a round % (a fixed £26 off £52.99 is 49%, sold as 50%).
    if (offer && offer.simple && entry.pct) pct = Number(entry.pct);
    // pctAsSold (benchmark-routine): the % exactly as the page sells it (45% on 2 + 1 free).
    if (offer && offer.pctAsSold && entry.pct) pct = Number(entry.pct);
    return { each: each, pct: pct };
  }

  function subscribeCtaFor(line) {
    var handles = new Set(cfg.goodieHandles || []);
    if (isGift(line) || linePlanId(line) || !handles.has(String(line.handle || ''))) return '';
    var offer = cartOffer(state.cart);
    if (offer && offer.plans && lineOffer(line) === offerKey(offer)) {
      // Once per offer cart, on its first one-time line.
      var firstOnce = offerLines(state.cart, offer, 'once')[0];
      if (!firstOnce || firstOnce.key !== line.key) return '';
      var tgt = offerSubTarget(state.cart, offer);
      if (!tgt) return '';
      var perB = subEffective(offer, tgt.entry).each;
      return '<button class="cd-sub" type="button" data-cd-subscribe-offer>'
        + '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span>Subscribe &amp; save' + (perB ? ' &middot; ' + esc(money(perB, (state.cart && state.cart.currency) || 'GBP')) + '/bottle' : '') + '</span>'
        + '</button>';
    }
    var entry = cfg.cadences[1];
    if (!entry) return '';
    var each = Number(line.original_price || line.price || 0);
    var per = each ? money(Math.round(each * 0.8), (state.cart && state.cart.currency) || 'GBP') : '';
    return '<button class="cd-sub" type="button" data-cd-subscribe="' + esc(line.key) + '">'
      + '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '<span>Subscribe &amp; save' + (per ? ' &middot; ' + esc(per) + '/bottle' : '') + '</span>'
      + '</button>';
  }

  function renderSubscribeCta(cart) {
    var handles = new Set(cfg.goodieHandles || []);
    var oneTime = ((cart.items) || []).filter(function (l) {
      return !isGift(l) && !linePlanId(l) && handles.has(String(l.handle || ''));
    });
    if (!oneTime.length) return '';
    var entry = cfg.cadences[1];
    if (!entry) return '';
    return '<button class="cd-sub" type="button" data-cd-subscribe>'
      + '<span>Subscribe &amp; save</span>'
      + '<span class="cd-sub__save">' + esc(entry.label) + '</span>'
      + '</button>';
  }

  /* Gifts collapse into one row rather than N near-identical lines: the
     customer cares that gifts are included and what they are worth, not that
     each one occupies a slot. Value is the sum of what the lines WOULD have
     cost, which is the number worth showing. */
  /* A free line is priced 0, so its worth is not in the cart — take it from
     config, falling back to whatever the line does carry. */
  function giftValue(line) {
    var v = (cfg.giftValues || {})[String(line.variant_id)];
    if (v) return Math.round(Number(v) * 100) * Number(line.quantity || 1);
    return Number(line.original_line_price || 0) || Number(line.original_price || 0) * Number(line.quantity || 1);
  }

  /* The next-order discount has no variant, so it can never be a cart line and
     can never reach checkout. It is drawn from the subscription subtotal instead:
     10% of what the plan will charge again next cycle. Same entitlement test as the
     welcome freebies, so it appears and disappears with them. */
  function nextOrderDiscount(cart) {
    var d = cfg.nextOrderDiscount;
    if (!d || !Number(d.percent)) return null;
    if (!hasSubscription(cart) || cartHasNewPlan(cart)) return null;
    /* An offer that gives a real gift in its place turns this off. */
    var offer = cartOffer(cart);
    if (offer && offer.nextOrder === false) return null;
    var base = ((cart && cart.items) || []).reduce(function (a, l) {
      if (isGift(l) || !linePlanId(l)) return a;
      return a + (Number(l.original_line_price || 0) || 0);
    }, 0);
    if (base <= 0) return null;
    return { name: d.name || 'Next order discount', value: Math.round(base * Number(d.percent) / 100) };
  }

  /* Rendered like a gift line but deliberately without a key, quantity control or
     remove button: there is nothing in the cart to act on. */
  function renderVirtualGift(g, cur) {
    return '<div class="cd-line cd-line--gift cd-line--virtual">'
      + '<div class="cd-line__media cd-line__media--mark">'
      +   '<svg viewBox="0 0 96 54" fill="none" aria-hidden="true">'
      +     '<rect x="29" y="10" width="38" height="36" rx="5" fill="none" stroke="currentColor" stroke-width="2.4"/>'
      +     '<path d="M29 21 H67" stroke="currentColor" stroke-width="2.4"/>'
      +     '<path d="M39 7 V13 M57 7 V13" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>'
      +     '<rect x="33" y="25" width="30" height="17" rx="3" fill="#d78a00"/>'
      +     '<text x="48" y="34.5" text-anchor="middle" dominant-baseline="central" font-size="11.5" font-weight="800" fill="#fff">'
      +       Number((cfg.nextOrderDiscount || {}).percent || 0) + '%</text>'
      +   '</svg>'
      + '</div>'
      + '<div class="cd-line__main">'
      +   '<div class="cd-line__top"><div><div class="cd-line__title">' + esc(g.name) + '</div></div>'
      +     '<span class="cd-line__tag">Free</span></div>'
      +   '<div class="cd-line__price">'
      +     (g.value > 0 ? '<s class="cd-line__was">' + esc(money(g.value, cur)) + '</s> ' : '')
      +     '<span class="cd-line__now">Free</span>'
      +   '</div>'
      + '</div>'
      + '</div>';
  }

  function renderGifts(gifts, cart) {
    var virt = nextOrderDiscount(cart);
    if (!gifts.length && !virt) return '';
    var cur = cart.currency || 'GBP';
    var count = gifts.reduce(function (a, l) { return a + Number(l.quantity || 1); }, 0) + (virt ? 1 : 0);
    var value = gifts.reduce(function (a, l) { return a + giftValue(l); }, 0) + (virt ? virt.value : 0);
    /* Not a native <details>: the browser hard-hides <details> content when
       closed, so it can't be height-animated cross-browser. A button + a
       grid-rows wrapper animates to the natural height with no magic numbers. */
    return '<div class="cd-gifts" data-cd-gifts>'
      + '<button class="cd-gifts__row" type="button" data-cd-gifts-toggle aria-expanded="false">'
      +   '<span class="cd-gifts__ico" aria-hidden="true">'
      +     '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 12h16v8H4zM2 8h20v4H2zM12 8v12M12 8S9 3 7 5s5 3 5 3zM12 8s3-5 5-3-5 3-5 3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>'
      +   '</span>'
      +   '<span class="cd-gifts__txt">Includes ' + count + ' free gift' + (count === 1 ? '' : 's')
      +     (value > 0 ? ' <span class="cd-gifts__inline">&middot; ' + esc(money(value, cur)) + ' value</span>' : '') + '</span>'
      +   (value > 0 ? '<span class="cd-gifts__val">' + esc(money(value, cur)) + ' value</span>' : '')
      +   '<svg class="cd-gifts__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</button>'
      + '<div class="cd-gifts__wrap"><div class="cd-gifts__list">'
      +   gifts.map(function (l) { return renderLine(l, cart); }).join('')
      +   (virt ? renderVirtualGift(virt, cur) : '')
      + '</div></div>'
      + '</div>';
  }

  /* ── upsells ─────────────────────────────────────────────────── */

  /* Offer carts (fermented, fermented2): the Goodie bottle cards sell the
     offer's next step at the price the customer will actually get, and add
     bottles tagged for the offer so they count toward it. A subscription offer
     cart drops the bottle cards (its bundle's own upgrade button is the step
     up), so a one-time bottle is never mixed into it. */
  /* One card for every upsell, in every rail: image, name (bold) with a
     subtitle under it (two lines reserved, so no name is cut off), the price
     with any struck-through compare-at, one note line (the offer hint or the
     free-shipping gap; reserved even when empty, so all cards line up), and
     the Add button. Goodie bottles read "Goodie" / "<Flavour> drink". */
  function upsellCard(u, o) {
    var cur = o.cur;
    var sub = u.flavour ? u.flavour + ' drink' : (u.subtitle || '');
    var addAttrs = ' data-cd-add="' + Number(u.variantId) + '"'
      + (o.qty > 1 || o.offer ? ' data-cd-add-qty="' + Math.max(1, o.qty || 1) + '"' : '')
      + (o.offer ? ' data-cd-add-offer' : '');
    return '<div class="cd-ups__card' + (o.bridge ? ' is-bridge' : '') + '">'
      + '<div class="cd-ups__top">'
      +   '<div class="cd-ups__media">' + (u.image ? '<img src="' + esc(u.image) + '" alt="" loading="lazy" width="56" height="56">' : '') + '</div>'
      +   '<div class="cd-ups__body">'
      +     '<div class="cd-ups__name">' + esc(u.title) + (sub ? '<span class="cd-ups__sub">' + esc(sub) + '</span>' : '') + '</div>'
      +     '<div class="cd-ups__meta"><span class="cd-ups__price">' + esc(money(o.now, cur)) + '</span>'
      +       (o.was > o.now ? '<s class="cd-ups__was">' + esc(money(o.was, cur)) + '</s>' : '') + '</div>'
      +     '<div class="cd-ups__note' + (o.bridge ? ' is-bridge' : '') + '">' + (o.note || '') + '</div>'
      +   '</div>'
      + '</div>'
      + '<button class="cd-ups__add" type="button"' + addAttrs + '>'
      +   '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg> Add' + (o.qty > 1 ? ' ' + o.qty : '')
      + '</button>'
      + '</div>';
  }

  function renderOfferUpsells(cart, offer) {
    var cur = cart.currency || 'GBP';
    var handles = new Set(cfg.goodieHandles || []);
    var isSub = offerLines(cart, offer, 'sub').length > 0;
    var inCart = new Set(((cart.items) || []).map(function (l) { return Number(l.variant_id); }));
    var list = (cfg.upsells || []).filter(function (u) {
      if (handles.has(String(u.handle || ''))) return !isSub;
      return !inCart.has(Number(u.variantId));
    });
    if (!list.length) return '';

    var paid = qtyOf(offerLines(cart, offer, 'once'));
    var need = 1, note = '', priceEach = null, wasEach = null;
    if (offer.packs) {
      var pk = packDiscount(cart);
      var target = pk.next || pk.tier;
      if (pk.next) need = Math.max(1, pk.next.min - paid);
      if (target) {
        note = pk.next ? 'add ' + need + ' &middot; ' + Math.round(Number(target.pct)) + '% off at ' + target.min : Math.round(Number(target.pct)) + '% off';
        priceEach = function (u) { return Math.round(Number(u.price) * (1 - Number(target.pct) / 100)); };
        wasEach = function (u) { return Number(u.price); };
      }
    } else if (offer.freeBottles && freeAppliesTo(offer, 'once')) {
      var keys = Object.keys(offer.freeBottles).map(Number).sort(function (a, b) { return a - b; });
      var nextK = keys.find(function (k) { return k > paid && Number(offer.freeBottles[k]) > Number(offer.freeBottles[paid] || 0); });
      if (nextK) {
        need = nextK - paid;
        var gain = Number(offer.freeBottles[nextK]) - Number(offer.freeBottles[paid] || 0);
        var freeNextK = Number(offer.freeBottles[nextK]);
        note = 'add ' + need + ' &middot; +' + gain + ' free bottle' + (gain === 1 ? '' : 's');
        // The price per bottle at that step, as the page shows it (paid spread
        // over every bottle received): 1 in the basket -> 32.66, 2 -> 29.39.
        priceEach = function (u) { return Math.round(Number(u.price) * nextK / (nextK + freeNextK)); };
        wasEach = function (u) { return Number(u.price); };
      } else {
        // Top deal reached: another bottle adds no free bottle, so no bottle
        // cards (the other upsells stay).
        list = list.filter(function (u) { return !handles.has(String(u.handle || '')); });
        if (!list.length) return '';
      }
    }

    var many = list.length > 1;
    return '<div class="cd-ups"' + (many ? ' data-cd-ups' : '') + '>'
      + '<div class="cd-ups__head"><span class="cd-ups__title">' + (isSub ? 'You might also like' : 'Add to your offer') + '</span></div>'
      + '<div class="cd-ups__wrap">'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--prev" type="button" data-cd-ups-prev aria-label="Previous" hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m15 6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '<div class="cd-ups__rail" data-cd-ups-rail>'
      + list.map(function (u) {
          var bottle = handles.has(String(u.handle || ''));
          return upsellCard(u, {
            cur: cur,
            now: bottle && priceEach ? priceEach(u) : Number(u.price),
            was: bottle && wasEach ? wasEach(u) : (Number(u.compareAt) > Number(u.price) ? Number(u.compareAt) : 0),
            note: bottle ? note : '',
            qty: bottle ? need : 1,
            offer: bottle
          });
        }).join('')
      + '</div>'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--next" type="button" data-cd-ups-next aria-label="Next"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '</div></div>';
  }

  /* Simple offers: their own bottles (the -offer products, a different base
     price from the BOGO bottles) plus the accessories. On a subscription cart
     the bottles are one-off adds at full price, never added to the plan. An
     accessory already in the cart (e.g. 42b's free cup) is not offered again.
     No free-shipping bridge: these products have no spend threshold. */
  function renderSimpleUpsells(cart, offer) {
    var cur = cart.currency || 'GBP';
    var goodie = new Set(cfg.goodieHandles || []);
    var inCart = new Set(((cart.items) || []).map(function (l) { return Number(l.variant_id); }));
    var isSub = simpleSubscribed(cart, offer);
    var bottles = (cfg.offerBottles || []).filter(function (u) { return Number(u.variantId) > 0; });
    var extras = (cfg.upsells || []).filter(function (u) {
      return !goodie.has(String(u.handle || '')) && !inCart.has(Number(u.variantId));
    });
    var list = bottles.map(function (u) { return { u: u, bottle: true }; })
      .concat(extras.map(function (u) { return { u: u, bottle: false }; }));
    if (!list.length) return '';
    var many = list.length > 1;
    return '<div class="cd-ups"' + (many ? ' data-cd-ups' : '') + '>'
      + '<div class="cd-ups__head"><span class="cd-ups__title">' + (isSub ? 'You might also like' : 'Add to your order') + '</span></div>'
      + '<div class="cd-ups__wrap">'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--prev" type="button" data-cd-ups-prev aria-label="Previous" hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m15 6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '<div class="cd-ups__rail" data-cd-ups-rail>'
      + list.map(function (it) {
          var u = it.u;
          return upsellCard(u, {
            cur: cur,
            now: Number(u.price),
            was: it.bottle ? 0 : (Number(u.compareAt) > Number(u.price) ? Number(u.compareAt) : 0),
            // Beside a subscription a bottle is a one-off, never added to the plan.
            note: it.bottle && isSub ? 'one-off, full price' : '',
            qty: 1,
            offer: it.bottle
          });
        }).join('')
      + '</div>'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--next" type="button" data-cd-ups-next aria-label="Next"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '</div></div>';
  }

  function renderUpsells(cart) {
    var offerU = cartOffer(cart);
    if (offerU && offerU.simple) return renderSimpleUpsells(cart, offerU);
    if (offerU && (offerU.plans || offerU.packs)) return renderOfferUpsells(cart, offerU);
    var inCart = new Set(((cart.items) || []).map(function (l) { return Number(l.variant_id); }));
    var list = (cfg.upsells || []).filter(function (u) { return !inCart.has(Number(u.variantId)); });
    if (!list.length) return '';
    var cur = cart.currency || 'GBP';

    /* Cheapest-path hint: the gap to free shipping, and the cheapest upsell that
       would clear it. That one gets a note and floats to the front of the rail,
       so the drawer nudges the smallest add that unlocks the reward. */
    var gap = (thresholds(cur).freeShipping * 100) - Number(cart.total_price || 0);
    var bridger = null;
    if (gap > 0 && !offerShipsFree(cart)) {
      list.slice().sort(function (a, b) { return a.price - b.price; }).some(function (u) {
        if (Number(u.price) >= gap) { bridger = Number(u.variantId); return true; }
        return false;
      });
    }
    if (bridger) {
      list = list.slice().sort(function (a, b) {
        return (Number(b.variantId) === bridger ? 1 : 0) - (Number(a.variantId) === bridger ? 1 : 0);
      });
    }

    var many = list.length > 1;
    return '<div class="cd-ups"' + (many ? ' data-cd-ups' : '') + '>'
      + '<div class="cd-ups__head"><span class="cd-ups__title">You might also like</span>'
      + (bridger ? '<span class="cd-ups__hint">cheapest path to free shipping</span>' : '') + '</div>'
      + '<div class="cd-ups__wrap">'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--prev" type="button" data-cd-ups-prev aria-label="Previous" hidden><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m15 6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '<div class="cd-ups__rail" data-cd-ups-rail>'
      + list.map(function (u) {
          var isBridge = Number(u.variantId) === bridger;
          return upsellCard(u, {
            cur: cur,
            now: Number(u.price),
            was: Number(u.compareAt) > Number(u.price) ? Number(u.compareAt) : 0,
            note: isBridge ? esc(money(gap, cur)) + ' to free shipping' : '',
            bridge: isBridge,
            qty: 1
          });
        }).join('')
      + '</div>'
      + (many ? '<button class="cd-ups__nav cd-ups__nav--next" type="button" data-cd-ups-next aria-label="Next"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '')
      + '</div></div>';
  }

  /* Arrow nav for the upsell rail — toggle button visibility at the ends. */
  function bindUpsellNav() {
    var rail = bodyEl.querySelector('[data-cd-ups-rail]');
    if (!rail) return;
    var prev = bodyEl.querySelector('[data-cd-ups-prev]');
    var next = bodyEl.querySelector('[data-cd-ups-next]');
    function sync() {
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      if (prev) prev.hidden = atStart;
      if (next) next.hidden = atEnd;
      // (Edge-fade mask removed — CSS masks on a scrolling flex container
      // produced random compositing artifacts on scroll and on render. The peek
      // + arrows are affordance enough.)
    }
    rail.addEventListener('scroll', sync, { passive: true });
    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -rail.clientWidth * 0.8, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left: rail.clientWidth * 0.8, behavior: 'smooth' }); });
    sync();
  }

  /* ── footer ──────────────────────────────────────────────────── */

  var TRUST_ICONS = {
    returns:  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    delivery: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="7" cy="18" r="1.6" stroke="currentColor" stroke-width="1.6"/><circle cx="17" cy="18" r="1.6" stroke="currentColor" stroke-width="1.6"/></svg>',
    secure:   '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'
  };

  /* Merchant-editable delivery estimate. Renders nothing at all when disabled
     or when no dates are set — an empty promise is worse than none. */
  /* Delivery widget. Dates are COMPUTED so it works without anyone filling
     settings in, but any manual field wins — during a delay the merchant needs
     to state what is true, not what a cutoff rule infers.
     Dispatch: next business day if past cutoff or on a weekend.
     Arrival: dispatch + 1-2 business days. */
  function addBusinessDays(d, n) {
    var out = new Date(d.getTime());
    while (n > 0) { out.setDate(out.getDate() + 1); if (out.getDay() !== 0 && out.getDay() !== 6) n--; }
    return out;
  }
  function fmtDay(d) {
    try { return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch (e) { return ''; }
  }
  function renderEta() {
    var e = cfg.eta || {};
    if (!e.enabled) return '';

    var ship = String(e.ship || '').trim();
    var arrive = String(e.arrive || '').trim();
    if (!ship || !arrive) {
      var now = new Date();
      var cutoff = Number(e.cutoffHour != null ? e.cutoffHour : 14);
      var dispatch = new Date(now.getTime());
      // Past cutoff, or a weekend, means it goes out the next working day.
      if (now.getHours() >= cutoff || now.getDay() === 0 || now.getDay() === 6) dispatch = addBusinessDays(now, 1);
      while (dispatch.getDay() === 0 || dispatch.getDay() === 6) dispatch = addBusinessDays(dispatch, 1);
      if (!ship) ship = fmtDay(dispatch);
      if (!arrive) arrive = fmtDay(addBusinessDays(dispatch, 2));
    }

    var carrier = String(e.carrier || '').trim();
    var second = [arrive ? 'Arrives by ' + esc(arrive) : '', carrier ? esc(carrier) : '']
      .filter(Boolean).join(' &middot; ');
    if (!ship && !second) return '';
    return '<div class=cd-eta>'
      + '<span class=cd-eta__ico aria-hidden=true>' + TRUST_ICONS.delivery + '</span>'
      + '<span class=cd-eta__txt>'
      +   (ship ? '<span class=cd-eta__ship>' + esc(e.shipLabel || 'Ships') + ' <b>' + esc(ship) + '</b></span>' : '')
      +   (second ? '<span class=cd-eta__sub>' + second + '</span>' : '')
      + '</span></div>';
  }

  /* Money computed once, in the order that stops anything double-counting. */
  function computeMoney(cart) {
    var cur = cart.currency || 'GBP';
    var subtotal = ((cart.items) || []).reduce(function (a, l) { return isGift(l) ? a : a + lineWas(l); }, 0);
    var pr = (state.discountCode && proxy.result && proxy.result.discountApplicable) ? proxy.result : null;
    var goods = pr ? Math.round(Number(pr.total || 0) * 100) : Number(cart.total_price || 0);
    /* One-time pack price (Functions app): whatever of it Shopify has not
       already applied in the cart comes off here, so the drawer total is the
       checkout total. Shown as its own row; the Discount row keeps codes. */
    var pack = packDiscount(cart);
    goods -= pack.pending;
    // The proxy prices the customer's code alone, without the pack code.
    if (pr && pack.tier && pack.applied > 0) goods -= pack.expected;
    var packOff = pack.expected;
    // Free-bottle offers: the free bottles' value joins the subtotal and comes
    // back off as the offer's saving, so the summary reads like the page.
    var fo = freeOfferSaving(cart);
    subtotal += fo.value;
    // giftsInSaving (benchmark): the free gift lines' worth (the Buy 1 Get 1 bottle,
    // the e-book) joins the subtotal, so it comes back off in the Discount row and
    // "You saved" counts the gifts the page sells, not just the 20%.
    var gOffer = cartOffer(cart);
    // (An offer with its own free-bottle row already counts that bottle: fo.)
    var foIds = fo.value > 0 ? (cfg.bogoGiftVariantIds || []).map(Number) : [];
    if (gOffer && gOffer.giftsInSaving) ((cart.items) || []).forEach(function (l) {
      if (isGift(l) && foIds.indexOf(Number(l.variant_id)) < 0) subtotal += giftValue(l);
    });
    // Simple offers: the subscription's first-order saving is the offer, so it
    // gets its own row rather than reading as a discount code.
    var so = simpleOffer(cart), planOff = 0;
    if (so) offerLines(cart, so, 'sub').forEach(function (l) { planOff += Math.max(0, lineWas(l) - lineNow(l)); });
    var codeOff = Math.max(0, subtotal - goods - packOff - fo.value - planOff);
    var feeFull = Math.round(Number(cfg.shippingFee || 0) * 100);
    // A free-shipping code waives the fee like the £50 threshold does, so the
    // total matches what checkout will charge. Simple offers have no threshold:
    // their products ship free only with the subscription (its delivery code).
    var freeShip = so
      ? (simpleSubscribed(cart, so) || codeIsFreeShipping(cart))
      : (goods >= thresholds(cur).freeShipping * 100 || codeIsFreeShipping(cart) || offerShipsFree(cart));
    var shipFee = freeShip ? 0 : feeFull;
    var shipSaving = freeShip ? feeFull : 0;
    var offerOff = packOff + fo.value + planOff;
    var offerLabel = packOff > 0 ? pack.qty + '-bottle pack price' : (planOff > 0 ? (so.saveLabel || 'Subscription saving') : fo.label);
    return { cur: cur, subtotal: subtotal, codeOff: codeOff, packOff: offerOff, packLabel: offerLabel, packQty: pack.qty, feeFull: feeFull,
             shipFee: shipFee, shipSaving: shipSaving, grandTotal: goods + shipFee };
  }

  /* Summary lives in the SCROLLABLE body, not the pinned dock — the prototype
     pins only the checkout button, so discount, totals and payment icons scroll
     with everything else. */
  function renderSummary(cart) {
    if (!cart.item_count) return '';
    var m = computeMoney(cart), cur = m.cur;
    return '<div class="cd-summary" data-cd-summary>'
      + renderDiscount()
      + '<div class="cd-sum">'
      +  '<div class="cd-sum__row"><span>Subtotal</span><span>' + esc(money(m.subtotal, cur)) + '</span></div>'
      +  (m.packOff > 0 ? '<div class="cd-sum__row cd-sum__row--save"><span>' + esc(m.packLabel) + '</span><span>−' + esc(money(m.packOff, cur)) + '</span></div>' : '')
      +  '<div class="cd-sum__row' + (m.codeOff > 0 ? ' cd-sum__row--save' : '') + '"><span>Discount</span><span>'
        + (m.codeOff > 0 ? '\u2212' + esc(money(m.codeOff, cur)) : esc(money(0, cur))) + '</span></div>'
      +  '<div class="cd-sum__row"><span>Shipping</span><span>'
        + (m.shipFee ? esc(money(m.shipFee, cur))
            : (m.feeFull ? '<s class="cd-sum__strike">' + esc(money(m.feeFull, cur)) + '</s> ' : '') + '<b class="cd-sum__free">' + esc(money(0, cur)) + '</b>')
        + '</span></div>'
      +  '<div class="cd-sum__row cd-sum__row--total"><span>Total</span><span>' + esc(money(m.grandTotal, cur)) + '</span></div>'
      +  ((m.codeOff + m.packOff + m.shipSaving) > 0 ? '<div class="cd-saved"><span>You saved</span><span>' + esc(money(m.codeOff + m.packOff + m.shipSaving, cur)) + '</span></div>' : '')
      + '</div>'
      + renderEta()
      + '<div class="cd-trust">'
      + (cfg.trust || []).map(function (t) {
          // Simple offers ship free with the subscription, not from £50; so does a
          // subscribed shipCode offer (benchmark).
          var so = simpleOffer(cart) || (offerShipsFree(cart) ? cartOffer(cart) : null);
          var txt = so && so.trustShip && t.icon === 'delivery' ? so.trustShip : t.text;
          return '<div class="cd-trust__item"><span class="cd-trust__ico">' + (TRUST_ICONS[t.icon] || '') + '</span>'
               + '<span class="cd-trust__txt">' + esc(txt) + '</span></div>';
        }).join('')
      + '</div>'
      + (cfg.paymentIcons ? '<div class="cd-pay"><img src="' + esc(cfg.paymentIcons) + '" alt="" loading="lazy" decoding="async"></div>' : '')
      + '</div>';
  }

  /* The ONLY pinned element. */
  function renderCheckout(cart) {
    if (!cart.item_count) { footEl.hidden = true; footEl.innerHTML = ''; return; }
    footEl.hidden = false;
    var m = computeMoney(cart);
    footEl.innerHTML =
      '<button class="cd-checkout" type="button" data-cd-checkout>'
      +   TRUST_ICONS.secure
      +   '<span>Secure Checkout</span><span>' + esc(money(m.grandTotal, m.cur)) + '</span>'
      + '</button>';
  }

  /* Applied-code pill label: code + its OWN value — a percentage for %-based
     codes (e.g. "GOODIE20 (−20%)"), otherwise a money amount ("SAVE5 (−£5.00)").
     If the proxy sends discountType ('percentage'|'fixed') + discountValue we use
     them; otherwise we infer from the code's amount vs the subtotal it applies to
     (a clean whole-% ⇒ percentage, else show the money off). */
  function codeTagLabel() {
    var code = state.discountCode || '';
    var pr = proxy.result;
    if (!code) return '';
    // Shopify's own cart knows the code's type and value for certain (it holds
    // the code whether it came from a link or the Apply button), so it wins
    // over the proxy's amount-based guess, which read £5 off £48.99 as "10%".
    var native = nativeCodeLabel(state.cart, code);
    if (native !== esc(code) || !pr || !pr.discountApplicable) return native;
    if (pr.discountType === 'free_shipping') return esc(code) + ' (free shipping)';
    var cur = pr.currencyCode || (state.cart && state.cart.currency) || 'GBP';
    if (pr.discountType === 'percentage' && pr.discountValue != null)
      return esc(code) + ' (−' + (+pr.discountValue) + '%)';
    if (pr.discountType === 'fixed' && pr.discountValue != null)
      return esc(code) + ' (−' + money(Math.round(pr.discountValue * 100), cur) + ')';
    var base = Number(pr.subtotal || 0), amt = Number(pr.discountAmount || 0);
    if (amt <= 0) return esc(code);
    if (base > 0 && Math.abs(amt / base * 100 - Math.round(amt / base * 100)) < 0.25)
      return esc(code) + ' (−' + Math.round(amt / base * 100) + '%)';
    return esc(code) + ' (−' + money(Math.round(amt * 100), cur) + ')';
  }

  function renderDiscount() {
    if (state.discountCode) {
      return '<div class="cd-code is-on">'
        + '<span class="cd-code__tag">' + codeTagLabel() + '</span>'
        + '<button class="cd-code__clear" type="button" data-cd-code-clear>Remove</button></div>';
    }
    return '<details class="cd-code"><summary class="cd-code__toggle">'
      + '<svg class="cd-code__tagico" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 12l-8 8-9-9V3h8l9 9z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="7.5" cy="7.5" r="1.4" fill="currentColor"/></svg>'
      + '<span>Have a discount code?</span>'
      + '<svg class="cd-code__chev" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</summary>'
      + '<div class="cd-code__row">'
      +   '<input class="cd-code__input" type="text" autocomplete="off" placeholder="Enter code" data-cd-code-input aria-label="Discount code">'
      +   '<button class="cd-code__apply" type="button" data-cd-code-apply>Apply</button>'
      + '</div><p class="cd-msg" data-cd-code-msg hidden></p></details>';
  }

  /* ── render ──────────────────────────────────────────────────── */

  /* The header badge is server-rendered, so it goes stale the moment the
     drawer changes anything. Keep it in step, including the is-empty rule
     that hides the button entirely at zero. */
  function syncHeaderCount(cart) {
    var n = Number(cart && cart.item_count || 0);
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = String(n); });
    document.querySelectorAll('[data-cart-toggle]').forEach(function (el) { el.classList.toggle('is-empty', n === 0); });
  }

  /* With a code live, re-price through the proxy after any cart change and
     repaint the footer — otherwise the discount silently goes stale the moment
     a quantity moves. */
  function reprice(cart) {
    if (!state.discountCode) return;
    fetchProxyCart(cart, true).then(function () {
      var box = bodyEl.querySelector('[data-cd-summary]');
      if (box) box.outerHTML = renderSummary(cart);
      renderCheckout(cart);
    });
  }

  function render(cart) {
    syncHeaderCount(cart);
    renderProgress(cart);

    if (!cart.item_count) {
      bodyEl.innerHTML =
        '<div class="cd-empty">'
        + '<span class="cd-empty__ico"><svg width="20" height="20" viewBox="0 0 24 24" fill="none">'
        +   '<path d="M6 7h12l-1 13H7zM9 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg></span>'
        + '<p class="cd-empty__title">Your cart is feeling light.</p>'
        + '<p class="cd-empty__body">Find your Goodie. Every bottle is fermented slowly, kept raw, and never heat-treated.</p>'
        + '<a class="btn btn--dark cd-empty__btn" href="/pages/goodie-e">Shop the Goodies</a>'
        + '</div>';
      renderCheckout(cart);
      checkUnlock();
      return;
    }

    var grouped = groupLines(cart);
    var gifts = (cart.items || []).filter(isGift);

    bodyEl.innerHTML =
      grouped.bundles.map(function (b) { return renderBundle(b, cart); }).join('')
      + grouped.singles.map(function (l) { return renderLine(l, cart); }).join('')
      + renderGifts(gifts, cart)
      + renderUpsells(cart)
      + renderSummary(cart);

    renderCheckout(cart);
    bindUpsellNav();
    checkUnlock();
    reprice(cart);
  }

  /* ── events ──────────────────────────────────────────────────── */

  function lineKeyFrom(el) {
    var line = el.closest('[data-line-key]');
    return line ? line.getAttribute('data-line-key') : null;
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cd-close]')) { close(); return; }

    var qtyBtn = e.target.closest('[data-cd-qty]');
    if (qtyBtn) {
      var key = lineKeyFrom(qtyBtn);
      var line = (state.cart.items || []).find(function (l) { return l.key === key; });
      if (!line) return;
      var next = Number(line.quantity || 1) + Number(qtyBtn.getAttribute('data-cd-qty'));
      act(function () { return postCart('/cart/change.js', { id: key, quantity: Math.max(0, next) }); });
      return;
    }

    if (e.target.closest('[data-cd-remove]')) {
      var rk = lineKeyFrom(e.target);
      if (rk) act(function () { return postCart('/cart/change.js', { id: rk, quantity: 0 }); });
      return;
    }

    // Removing a subscription bundle drops every line it groups, in one go.
    var rb = e.target.closest('[data-cd-remove-bundle]');
    if (rb) {
      var bkey = rb.getAttribute('data-cd-remove-bundle');
      act(async function () {
        var g = groupLines(state.cart);
        var bundle = g.bundles.find(function (b) { return b.key === bkey; });
        if (!bundle) return;
        await removeLines(bundle.lines);
      });
      return;
    }

    var add = e.target.closest('[data-cd-add]');
    if (add) {
      var vid = Number(add.getAttribute('data-cd-add'));
      var addItem = { id: vid, quantity: Math.max(1, Number(add.getAttribute('data-cd-add-qty') || 1)) };
      // Offer bottle cards: tag the bottles for the cart's offer (and the pack
      // price on pack offers) so they count toward it like the page's own adds.
      if (add.hasAttribute('data-cd-add-offer')) {
        var offerA = cartOffer(state.cart);
        var tagA = offerKey(offerA);
        if (tagA) {
          addItem.properties = { _offer: tagA };
          if (offerA.packs) addItem.properties._pack = 'otp';
        }
      }
      add.disabled = true;
      act(function () { return postCart('/cart/add.js', { items: [addItem] }); });
      return;
    }

    var subOffer = e.target.closest('[data-cd-subscribe-offer]');
    if (subOffer) {
      var offerS = cartOffer(state.cart);
      var tgtS = offerSubTarget(state.cart, offerS);
      if (!tgtS) return;
      subOffer.disabled = true;
      act(async function () {
        var lines = offerLines(state.cart, offerS, 'once');
        var chosen = [];
        lines.forEach(function (l) { for (var i = 0; i < Number(l.quantity || 1); i++) chosen.push(Number(l.variant_id)); });
        // Simple offers subscribe the bottles as they are (one plan, any count).
        var want = offerS.simple ? chosen.length : Number(tgtS.entry.qty || chosen.length);
        var cycle = flavourCycle();
        var counts = {};
        for (var i = 0; i < want; i++) {
          var v = chosen[i] != null ? chosen[i] : (cycle.length ? cycle[i % cycle.length] : chosen[0]);
          counts[v] = (counts[v] || 0) + 1;
        }
        var props = Object.assign({}, (lines[0] && lines[0].properties) || {});
        delete props._pack;
        var readd = Object.keys(counts).map(function (v) {
          return { id: Number(v), quantity: counts[v], selling_plan: Number(tgtS.entry.plan), properties: Object.assign({}, props) };
        });
        await removeLines(lines);
        if (readd.length) await postCart('/cart/add.js', { items: readd });
      });
      return;
    }

    var sub = e.target.closest('[data-cd-subscribe]');
    if (sub) {
      var entry = cfg.cadences[1];
      if (!entry) return;
      var onlyKey = sub.getAttribute('data-cd-subscribe');
      sub.disabled = true;
      act(async function () {
        var handles = new Set(cfg.goodieHandles || []);
        var lines = (state.cart.items || []).filter(function (l) {
          if (onlyKey && l.key !== onlyKey) return false;
          return !isGift(l) && !linePlanId(l) && handles.has(String(l.handle || ''));
        });
        var readd = lines.map(function (l) {
          return { id: Number(l.variant_id), quantity: Number(l.quantity || 1),
                   selling_plan: Number(entry.plan),
                   properties: Object.assign({}, l.properties || {}) };
        });
        await removeLines(lines);
        if (readd.length) await postCart('/cart/add.js', { items: readd });
      });
      return;
    }

    var gifts = e.target.closest('[data-cd-gifts-toggle]');
    if (gifts) {
      var box = gifts.closest('[data-cd-gifts]');
      var wrap = box && box.querySelector('.cd-gifts__wrap');
      if (box && wrap) {
        var openNow = !box.classList.contains('is-open');
        gifts.setAttribute('aria-expanded', openNow ? 'true' : 'false');
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        /* Measure the real from/to heights and tween between them with the Web
           Animations API. CSS height/grid transitions to `auto` don't fire
           reliably across browsers; this does, because both endpoints are real
           pixel values. The class holds the final height (0 or auto) after. */
        if (reduce || !wrap.animate) {
          box.classList.toggle('is-open', openNow);
        } else {
          var from = wrap.offsetHeight;
          box.classList.toggle('is-open', openNow);   // -> height:auto or 0
          var to = wrap.offsetHeight;
          wrap.animate(
            [{ height: from + 'px' }, { height: to + 'px' }],
            { duration: 280, easing: 'cubic-bezier(.4,0,.2,1)' }
          );
        }
      }
      return;
    }

    var toggle = e.target.closest('[data-cd-cad-toggle]');
    if (toggle) {
      var wrap = toggle.closest('[data-cd-cad]');
      var menu = wrap && wrap.querySelector('.cd-cad__menu');
      var openNow = menu && menu.hidden;
      closeCadMenus();
      if (menu && openNow) { menu.hidden = false; toggle.setAttribute('aria-expanded', 'true'); }
      return;
    }

    var up = e.target.closest('[data-cd-cadence]');
    if (up) {
      up.disabled = true;
      closeCadMenus();
      switchCadence(up.getAttribute('data-cd-bundle'), up.getAttribute('data-cd-cadence'));
      return;
    }

    var one = e.target.closest('[data-cd-onetime]');
    if (one) {
      one.disabled = true;
      closeCadMenus();
      switchToOneTime(one.getAttribute('data-cd-bundle'));
      return;
    }

    // A click anywhere else in the drawer closes an open cadence menu.
    if (!e.target.closest('.cd-cad__menu')) closeCadMenus();

    var applyBtn = e.target.closest('[data-cd-code-apply]');
    if (applyBtn) {
      var input = bodyEl.querySelector('[data-cd-code-input]');
      var msg = bodyEl.querySelector('[data-cd-code-msg]');
      var code = (input && input.value || '').trim();
      if (!code) return;
      applyDiscount(code, applyBtn).then(function (r) {
        if (r.ok) { refresh(); return; }
        if (msg) { msg.hidden = false; msg.textContent = r.msg; }
      });
      return;
    }

    if (e.target.closest('[data-cd-code-clear]')) {
      state.discountCode = null;
      invalidateProxy();
      // Clears Shopify's own cart code too, or a code from a discount link
      // would be read straight back on the next refresh.
      act(function () { return postCart('/cart/update.js', { attributes: { discount_code: '' }, discount: '' }); });
      return;
    }



    var checkoutBtn = e.target.closest('[data-cd-checkout]');
    if (checkoutBtn) {
      // Check out through Shopify's NATIVE cart, not the proxy's checkoutUrl:
      // the proxy rebuilds a Storefront cart from paid lines only, which silently
      // drops the free gift lines so they never get ordered. The gifts are real
      // £0 lines in the native cart, so /checkout carries them correctly. A
      // discount code is applied via Shopify's discount link, which keeps the
      // current cart (gifts included) intact.
      checkoutBtn.disabled = true;
      var code = state.discountCode;
      window.location.href = code
        ? '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout')
        : '/checkout';
    }
  });

  root.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-cd-slot]');
    if (!sel) return;
    var slot = sel.closest('[data-slot-line]');
    if (!slot) return;
    var fromKey = slot.getAttribute('data-slot-line');
    var fromVariant = Number(slot.getAttribute('data-slot-variant'));
    var toVariant = Number(sel.value);
    if (!toVariant || toVariant === fromVariant) return;

    act(async function () {
      var line = (state.cart.items || []).find(function (l) { return l.key === fromKey; });
      if (!line) return;
      var plan = linePlanId(line);
      var props = Object.assign({}, line.properties || {});
      var qty = Number(line.quantity || 1);
      // Swap ONE bottle: decrement the source line, add one of the new flavour
      // on the same plan carrying the same offer tag, so entitlement is intact.
      await postCart('/cart/change.js', { id: fromKey, quantity: Math.max(0, qty - 1) });
      await postCart('/cart/add.js', { items: [{
        id: toVariant, quantity: 1,
        selling_plan: plan ? Number(plan) : undefined,
        properties: props
      }] });
    });
  });

  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (e.target.matches('[data-cd-code-input]')) { e.preventDefault(); var b = bodyEl.querySelector('[data-cd-code-apply]'); if (b) b.click(); }
  });

  document.addEventListener('keydown', function (e) {
    if (root.hidden) return;
    if (e.key === 'Escape') { if (root.querySelector('.cd-cad__menu:not([hidden])')) closeCadMenus(); else close(); }
    trapFocus(e);
  });

  /* Header button + anything else that wants to open the drawer. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    e.preventDefault();
    open();
  });

  // Back button from checkout restores the page from the bfcache with the
  // checkout button still disabled from the outbound click. Re-enable it and
  // re-sync so the cart is usable again without a manual reload.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var btn = root.querySelector('[data-cd-checkout]');
    if (btn) btn.disabled = false;
    if (pageMode || (!root.hidden && root.classList.contains('is-open'))) refresh();
  });

  /* Straight to checkout without showing the drawer (the goodiev41 -nd
     templates). The gift lines only exist because refresh() reconciles them,
     so run the same three syncs first, then leave the way the drawer's own
     checkout button does: native /checkout, through the discount link when a
     code is saved. Never throws - whatever fails, the customer still reaches
     checkout with what is in the cart. */
  async function checkout() {
    var code = state.discountCode;
    try {
      var cart = await getCart();
      if (await stripOrphanGifts(cart)) cart = await getCart();
      if (await syncFreeGifts(cart)) cart = await getCart();
      if (await syncBogo(cart)) cart = await getCart();
      if (await syncPackCode(cart)) cart = await getCart();
      if (await syncCodedGifts(cart)) cart = await getCart();
      if (await syncOfferCodes(cart)) cart = await getCart();
      if (await syncHiddenGift(cart)) cart = await getCart();
      await syncOfferAttr(cart);
      if (code === null) code = (cart.attributes || {}).discount_code || null;
    } catch (e) {}
    window.location.href = code
      ? '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout')
      : '/checkout';
  }

  window.CannumoCart = { open: open, close: close, refresh: refresh, checkout: checkout };

  // Inline cart page: render immediately (the script is deferred, so the DOM is
  // ready). All the same syncing runs on first paint just as it does on open().
  if (pageMode) {
    root.hidden = false;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
    else refresh();
  }
})();
